import {
  isValidArray,
  isValidArrayAndNotEmpty, isValidObject, isValidObjectAndNotEmpty, truncateUUID,
} from 'components/feature/Report/ReportSidebar/common/helpers';
import { EndOfDayTime, IDateRangeTypes } from 'core/constants/date';
import { FilterType, NumberFilterTypes } from 'core/constants/filter';
import { DateBinningFormatters } from 'core/constants/formatters';
import { PaginationKey, defaultPageValue } from 'core/constants/pagination';
import { APIRequestStatus } from 'core/constants/redux';
import { v4 as uuidv4 } from 'uuid';
import {
  statusCodes, DimensionMode,
  DimensionStatus, ColumnDataTypes,
  FieldEntitiesType, draggableItemType, ZeroAnchorStatus,
  ColumnModes, JobTypes, dynamicMeasureDefaultConfig,
  DataRefreshEntityTypes,
  MaxRowGroupingDimensionsToAllowSubTotals,
  DimensionNoValue,
  DimensionNumericNoValue,
  OperatorTypes,
  DimensionNullValue,
} from 'core/constants/report';
import {
  ReportReduxModel, ReportResponseModel, AuthReduxModel, ObjModel, FilterModel,
} from 'core/models';
import { IBuilderFilter, SortingOrderTypes } from 'core/models/report-builder/report-builder';
import { IDimension, IVisualization } from 'core/models/report-redux';
import {
  IColumn, IFilterConfig, IJob, DynamicMeasureResponse, IConfigVisualization, IDataRefreshStatus,
  IDimensionMetaField,
  ISetDimensionsEntityGroupingPayload,
  ISetMetaSelectorOptionsPayload,
  ISetEntityGroupingPayload,
} from 'core/models/report-response';
import {
  getDatesIfRelativeRangeIsRestricted, getDatesIfAbsoluteRangeIsRestricted,
} from 'core/utils/date.util';
import FilterFetchUtility from 'core/utils/filter-fetch-utility';
import { hasPivotColumn, isListReport } from 'core/utils/report-builder.util';
import { buildCustomMeasure, getDataTypeFromCustomColumn, buildMeasureColumn } from 'core/utils/report-expressions';
import {
  columnsToDimensions, reOrderMeasures, isMeasure, rebuildSortState,
  getAppliedMeasures, sortColumns, isMeasureDynamic, isMeasureCustom,
  validateAndAppendCountToName, isMeasureAndApplied, areDimensionsEqual, isPieOrDonutChart,
  getEntityKey,
  getUniqIdentiferForDrilldownCols,
  getDestinationDimensionStatus,
  updateColumnsDisplayName,
  isCustomHierarchyColumnOrDimension,
  resolveWrappedHeader,
} from 'core/utils/report.util';
import {
  isEntityGrouped,
  updateEntityGroupedDimension,
  generateEntityStore,
  createEntityGroupedDimension,
  createEntityGroupedDrilldown,
  updateEntityGroupedDrilldown,
  addDimensionToGrouping,
  isDrilldownTabActive,
  getUpdatedMetaSelectorOptions,
  getOriginalDimensionIndex,
  isValidMetaSelectorOptions,
  updateEntityGroupStore,
  createEntityGroupedColumn,
  getMetaFieldId,
} from 'core/utils/entity-grouping.util';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import clone from 'lodash/clone';
import isArray from 'lodash/isArray';
import moment from 'moment-timezone';

import { buildCustomFiltersFromDefinitions, getMappedMetaFields, reconstructCustomFilters } from 'core/utils/common.utility';
import { filterBuilderToFilterConfig, getAllDependentFilterIds, reOrderDimensions } from 'redux-v2/report-builder/report-builder-store.utils';
import { IVariables } from 'core/models/store';
import { IChartAttributesKeys } from 'components/feature/Report/ReportSidebar/ChartsVisualization/chart-visualization.type';
import { MeasureIdentifier } from 'redux-v2/report-builder/report-builder-store.state';
import { calculateCustomHierarchyDrilldownColumnsFromHierarchies } from 'redux-v2/custom-hierarchy/custom-hierarchy.util';
import { CUSTOM_HIERARCHY_FILTER_SUFFIX } from 'core/constants/common';
import { IReportState, ReportActionTypes } from './report-store.state';

const getUserFilterTypeValue = (item: ReportResponseModel.IFilterConfig, reportConfig: ReportResponseModel.IReportConfig) => {
  let value = null;

  if (item.Type === FilterType.UserMultiSelect && item.IsPrimaryUserFilter && reportConfig.FiltersMetadata?.UserDropdownSelectType && item.DefaultValue) {
    value = {
      Type: reportConfig.FiltersMetadata.UserDropdownSelectType,
      UserIds: item.DefaultValue,
    };
  } else if (item.Type === FilterType.UserMultiSelect && !item.IsPrimaryUserFilter) {
    value = {
      Type: item?.DropdownMetaData?.DropdownSelectType,
      UserIds: item.DefaultValue,
    };
  } else if (NumberFilterTypes.includes(item.Type)) {
    value = { ...(isValidObject(item.DefaultValue) ? item.DefaultValue : { Value: item.DefaultValue ?? {} }), Operator: item.Operator };
  } else {
    value = item.DefaultValue;
  }
  return value;
};

const getFilterConfigItemValue = (item: ReportResponseModel.IFilterConfig) => item.IsDynamic || item.IsAdditional;

// Returns the updated report State on cofig load success
// @state - Report State
// @reportConfig - Loaded Report config request
// @variables - auth store varables to update the filter value
// @status - API status to check deleted report
export const getStateOnReportConfigLoadSuccess = (
  state: IReportState,
  reportConfig: ReportResponseModel.IReportConfig,
  variables: AuthReduxModel.IVariables,
  status: number,
): IReportState => {
  if (status === statusCodes.NoContent) {
    return state;
  }

  const newState = initializeNewState(state, reportConfig, variables);
  newState.accessStatusCode = status;
  newState.reportName = reportConfig?.Details?.Title;
  newState.reportDescription = reportConfig?.Details?.Description;
  updateColumnsDisplayName(reportConfig?.Columns);
  let dimensions = columnsToDimensions(reportConfig.Columns) || [];

  newState.allMeasures = reOrderMeasures(reportConfig.Columns.filter((c) => isMeasure(c) && !(c?.Props?.Measure?.IsCustomExpression && c?.Props?.Mode === ColumnModes.NotApplied)));
  newState.allMeasures = handleCustomMeasures(newState.allMeasures);

  dimensions = reOrderDimensions([...dimensions]);
  newState.appliedDimensions = dimensions;
  newState.activeDimensions = dimensions;
  newState.columns = getColumnsForActiveDimensions(reportConfig.Columns, dimensions, newState.allMeasures, newState.dynamicDimensions,
    newState.entityGroupStore, newState.entityGroupOptionsMap);

  newState.pivotBinningType = getPivotBinningType(newState.appliedDimensions);

  const definitions = newState.reportConfig.CustomFilterDefinitions;
  const processedFilters = definitions?.length
    ? buildCustomFiltersFromDefinitions(definitions, newState.reportConfig.Filters)
    : reconstructCustomFilters(newState.reportConfig.Filters);
  newState.filterConfig = getUpdatedFilterConfig(processedFilters, newState.reportConfig.FiltersMetadata);
  newState.appliedFilters = getAppliedFilters(processedFilters, variables, newState.reportConfig);
  newState.activeDimensionsLength = getAppliedDimension(newState.appliedDimensions);

  newState.sort = getSort(reportConfig.DefaultSort, newState.appliedDimensions, newState.columns);
  newState.isMultiSort = newState.sort.length > 1;

  newState.page = reportConfig.DefaultPage;

  const visualization = getInitialVisualization(reportConfig.Visualization, reportConfig?.IsAugmented);
  newState.entityGroupStore = generateEntityStore(newState.appliedDimensions, state.entityGroupStore);
  newState.Visualization = visualization;
  newState.selectedChartData.attributes = visualization.attributes;
  newState.selectedChartData.type = visualization.type;

  return newState;
};

// Returns the updated report State after intalizing filters and report config.
// @state - Report State
// @reportConfig - Loaded Report config request
// @variables - auth store varables to update the filter value
const initializeNewState = (state: IReportState, reportConfig: ReportResponseModel.IReportConfig, variables: AuthReduxModel.IVariables): IReportState => {
  const newState = state;
  const fetchFilter = new FilterFetchUtility(variables);
  const newAppliedFilters: ObjModel.ObjGeneric<FilterModel.IFilterResponse> = {};

  if (reportConfig?.Filters && !isEmpty(reportConfig.Filters)) {
    reportConfig.Filters.forEach((item) => {
      item.IsDynamic = getFilterConfigItemValue(item);
      const value = getUserFilterTypeValue(item, reportConfig);
      if (item.Type === FilterType.UserMultiSelect && reportConfig?.FiltersMetadata?.Status) {
        item.Metadata.Status = reportConfig?.FiltersMetadata?.Status;
      }
      newAppliedFilters[item.ID] = fetchFilter.GetDefaultSingleFilter(item, value);
    });
  }
  newState.reportConfig = reportConfig;

  newState.selectedToolbarMenuItem = 0;

  return newState;
};

// Returns the custom measures array if any.
// @measures - measures comming from report config.
const handleCustomMeasures = (measures: ReportResponseModel.IColumn[]): ReportResponseModel.IColumn[] => (isValidArrayAndNotEmpty(measures) ? measures.map((item) => {
  if (item?.Props?.Measure?.IsCustomExpression) {
    const customMeasure = buildCustomMeasure(
      item.Name,
      item.Props.Mode,
      item?.Props?.Measure.Expression,
      item.Id,
      item?.Props?.Formatter,
      getDataTypeFromCustomColumn(item?.Props?.Measure.Expression),
      item?.Props?.Measure?.RenderType,
      item?.Props?.IsSourceCustomColumn,
      item?.Props?.Description,
    );
    customMeasure.Props.AllowDrillDown = item.Props.AllowDrillDown;
    return customMeasure;
  }
  return item;
}) : []);

// Returns the pivot binning type if any date field applied in column grouping else set it as undefined.
// @dimensions - dimensions comming from report config columns.
const getPivotBinningType = (dimensions: ReportReduxModel.IDimension[]): string => dimensions.find((dim) => dim?.DimensionMode !== DimensionMode.RowGroup && dim?.Applied !== DimensionStatus.NotApplied
&& dim?.DimensionProp?.DataType === ColumnDataTypes.DateTime)?.DimensionProp?.Props?.BinningType;

// Returns the updated applied filter values coming from repotrconfig.
// @filters - Filter config coming from report config.
// @reportConfig - Loaded Report config request.
// @variables - auth store varables to update the filter value.
const getAppliedFilters = (filters: ReportResponseModel.IFilterConfig[], variables: AuthReduxModel.IVariables, reportConfig:ReportResponseModel.IReportConfig): ObjModel.ObjGeneric<FilterModel.IFilterResponse> => {
  const fetchFilter = new FilterFetchUtility(variables);
  const newAppliedFilters: ObjModel.ObjGeneric<FilterModel.IFilterResponse> = {};

  if (filters && !isEmpty(filters)) {
    filters.forEach((item) => {
      item.IsDynamic = getFilterConfigItemValue(item);
      const value = getUserFilterTypeValue(item, reportConfig);
      newAppliedFilters[item.ID] = fetchFilter.GetDefaultSingleFilter(item, value);
    });
  }

  return newAppliedFilters;
};

// Returns the sort state based on the report config.
// @defaultSort - defaultSort state coming from report config.
// @appliedDimensions - appliedDimensions columns coming from report config.
// @columns - columns coming from report config.
export const getSort = (defaultSort: ReportResponseModel.ISortedField[], appliedDimensions: ReportReduxModel.IDimension[], columns: ReportResponseModel.IColumn[], isReportListType?: boolean): ReportResponseModel.ISortedField[] => {
  if (defaultSort && !isEmpty(defaultSort)) {
    if (appliedDimensions && getAppliedDimension(appliedDimensions) > 1) {
      return rebuildSortState(defaultSort, appliedDimensions, getAppliedMeasures(columns), [], defaultSort?.length > 1, false, isReportListType);
    }
    return defaultSort;
  }
  return [];
};

// Returns the updated report State on filters load request.
// @state - Report State
// @filterId - filterID for which api call request
// @variables - auth store varables to update the filter value
// @isLazyLoad - boolean to check the lazyLoad
export const getStateOnFilterLoadRequest = (
  state: IReportState, filterId: string, variables: AuthReduxModel.IVariables,
  isLazyLoad?: boolean,
) => {
  const newState = state;
  const filtersLoading = cloneDeep(newState.requestProcessing[ReportActionTypes.FILTER_LOAD_REQUEST]);
  if (isLazyLoad) {
    filtersLoading[filterId] = APIRequestStatus.LazyLoading;
    newState.requestProcessing[ReportActionTypes.FILTER_LOAD_REQUEST] = filtersLoading;
  } else {
    const stateClone = cloneDeep(newState);
    const reportId = stateClone.reportId;
    const fetchFilter = new FilterFetchUtility({ reportId, ...variables });
    const appliedFilters = cloneDeep(newState.appliedFilters);
    filtersLoading[filterId] = APIRequestStatus.Processing;
    newState.requestProcessing[ReportActionTypes.FILTER_LOAD_REQUEST] = filtersLoading;
    if (newState.filterConfig && !isEmpty(newState.filterConfig)) {
      newState.filterConfig.forEach((item) => {
        if (item?.LinkedTo === filterId) {
          if (appliedFilters[item.ID]?.shouldLoad) {
            return;
          }
          appliedFilters[item.ID] = fetchFilter.GetDefaultSingleFilter(item, null);
        }
      });
    }
    newState.appliedFilters = appliedFilters;
  }
  return newState;
};
// Returns the updated report State on filter load success
// @state - Report State
// @filterId - filterID for which api call request
// @filterRsponse - Filter Respomse and setting in the state
export const getStateOnFilterLoadSuccess = (
  state: IReportState, filterId: string, filterResponse: FilterModel.IFilterResponse,
) => {
  const newState = state;
  const filtersLoading = clone(newState.requestProcessing[ReportActionTypes.FILTER_LOAD_REQUEST]);
  const newAppliedFilters = cloneDeep(newState.appliedFilters);
  filtersLoading[filterId] = APIRequestStatus.Success;
  let isUsersModifiedFlag = false;
  Object.values(filtersLoading).forEach((status) => {
    if (status === APIRequestStatus.Processing) isUsersModifiedFlag = true;
  });
  newState.isUsersModified = isUsersModifiedFlag;
  newAppliedFilters[filterId] = filterResponse;
  newState.appliedFilters = newAppliedFilters;
  newState.requestProcessing[ReportActionTypes.FILTER_LOAD_REQUEST] = filtersLoading;
  return newState;
};
// Returns the updated report State on filter load failure
// @state - Report State
// @filterId - filterID for which api call request
export const getStateOnFilterLoadFailure = (
  state: IReportState, filterId: string,
) => {
  const newState = state;
  const filtersLoading = cloneDeep(newState.requestProcessing[ReportActionTypes.FILTER_LOAD_REQUEST]);
  filtersLoading[filterId] = APIRequestStatus.Failure;
  newState.requestProcessing[ReportActionTypes.FILTER_LOAD_REQUEST] = filtersLoading;
  newState.requestProcessing[ReportActionTypes.REPORT_DATA_LOAD_REQUEST] = APIRequestStatus.Success;
  return newState;
};
// Returns the updated report State on report data load success
// @state - Report State
// @reportData - report Data for the report.
export const getStateOnReportDataLoadSuccess = (
  state: IReportState, reportData: ReportResponseModel.IReportData,
) => {
  const newState = state;
  if (!isEmpty(newState.customHierarchyDrilldownData)) {
    newState.customHierarchyDrilldownData.reportData = reportData;
  }
  newState.reportData = reportData;
  newState.columns = getColumnsForActiveDimensions(
    newState.columns, newState.activeDimensions, newState.allMeasures, newState.dynamicDimensions,
    newState.entityGroupStore, newState.entityGroupOptionsMap, newState.reportConfig.IsJoined,
  );
  newState.requestProcessing[ReportActionTypes.REPORT_DATA_LOAD_REQUEST] = APIRequestStatus.Success;
  newState.isResetClicked = false;
  newState.enablesSaveAs = newState.trackUserDrilldown ? newState.enablesSaveAs : !newState.isFirstLoad;
  newState.isFirstLoad = false;
  newState.showOthersInfoPopUp = reportData?.Raw?.HasOthersColumn || false;
  newState.mappedMetaFields = getMappedMetaFields(newState.columns, newState.reportData?.Raw?.Data) || null;
  newState.showReport = true;
  newState.trackUserDrilldown = false;
  // Reset the flag after report data is loaded
  newState.travelBackToMainReportFromCustomHierarchyReport = false;
  return newState;
};

// Returns the updated report State on report data load failure
// @state - Report State
export const getStateOnReportDataLoadFailure = (state: IReportState) => {
  const newState = state;
  newState.requestProcessing[ReportActionTypes.REPORT_DATA_LOAD_REQUEST] = APIRequestStatus.Failure;
  newState.isResetClicked = false;
  return newState;
};

// Returns the updated report State on report data load failure
// @state - Report State
export const getStateOnFilterChange = (
  state: IReportState, filterId: string, filterValue: any, variables: AuthReduxModel.IVariables,
  useChangedFilters: boolean = false,
) => {
  const newState = state;
  const stateClone = cloneDeep(newState);
  const reportId = stateClone.reportId;
  const fetchFilter = new FilterFetchUtility({ reportId, ...variables });
  const newAppliedFilters = cloneDeep(newState.appliedFilters);
  const selectedFilterConfig = newState.filterConfig.find((item:IFilterConfig) => item.ID === filterId);
  // Augment the lookup pool with sub-query (SubBuilder) filters so the dependency chain can reach
  // dependents whose parent (or whose own definition) lives in a sub-query. The main filterConfig
  // only carries sub-query filters flagged ShowInMainReport; the rest live in
  // savedReportConfig.SubBuilders. Passing isSubBuilder=false to filterBuilderToFilterConfig
  // includes ALL sub-query filters (skips the ShowInMainReport gating) and recurses SubFilters.
  // Scoped to the dependency-id calculation only; the reset loop below still iterates filterConfig.
  const subQueryFilters = (newState.savedReportConfig?.SubBuilders || []).flatMap(
    (builder) => filterBuilderToFilterConfig(builder.Filter, [], [], false),
  );
  const dependencyLookupFilterConfig = isValidArrayAndNotEmpty(subQueryFilters)
    ? [...newState.filterConfig, ...subQueryFilters]
    : newState.filterConfig;
  // Use getAllDependentFilterIds to handle the full dependency chain including custom filter constituents
  const allDependentFilterIds = getAllDependentFilterIds(
    filterId, dependencyLookupFilterConfig, newState.reportConfig?.CustomFilterDefinitions,
  );
  if (newState.filterConfig && !isEmpty(newState.filterConfig)) {
    newState.filterConfig.forEach((item) => {
      if (item.ID === filterId && newAppliedFilters[item.ID]) {
        newAppliedFilters[filterId].FilterResponse = filterValue;
      } else if (
        allDependentFilterIds.includes(item.ID)
        || (selectedFilterConfig?.Entity === FieldEntitiesType.User && item.Type === FilterType.UserMultiSelect && item.ID === selectedFilterConfig.ID)
      ) {
        // Skip constituents — their parent custom filter (found via LinkedTo) handles the reload.
        // Gate on `!IsCustomFilter`: the wrapper built by buildCustomFiltersFromDefinitions spreads
        // its FilterKey constituent and inherits that constituent's AssociatedCustomFilterId, so a
        // report-page wrapper carries AssociatedCustomFilterId === self.ID. Without the carve-out
        // the wrapper — the legitimate dependent that must reload when its parent changes — gets
        // skipped here, and its dropdown options never re-scope. Same guard shape as report.util.ts
        // and getNonDisabledFiltersCount in report-sidebar.component.tsx.
        if (item.AssociatedCustomFilterId && !item.IsCustomFilter) return;

        newAppliedFilters[item.ID] = fetchFilter.GetDefaultSingleFilter(item, null);
        newState.isUsersModified = true;
      }
    });
  }
  newState.appliedFilters = newAppliedFilters;
  if (!isEmpty(newState.customHierarchyDrilldownData)) {
    const prevAppliedFilters = cloneDeep(newState.prevAppliedFilters);
    prevAppliedFilters[prevAppliedFilters?.length - 1] = newAppliedFilters;
    newState.prevAppliedFilters = prevAppliedFilters;
  }
  newState.useChangedFilters = useChangedFilters;
  return newState;
};

// Returns the updated report State on filter change for custom hierarchy
// @state - Report State
// @filterId - Changed filterId
// @filterValue - New filter value
// @variables - auth store variables
// @useChangedFilters - boolean to trigger filter change
// @customHierarchyId - Custom Hierarchy ID
// @drilldownValue - drilldown level value
// @user - current user
export const getStateOnFilterChangeForCustomHierarchy = (
  state: IReportState,
  filterId: string,
  filterValue: any,
  variables: AuthReduxModel.IVariables,
  useChangedFilters: boolean = false,
  customHierarchyId?: string,
  drilldownValue?: any,
  user?: string,
) => {
  const newState = state;
  const stateClone = cloneDeep(newState);
  const reportId = stateClone.reportId;
  const fetchFilter = new FilterFetchUtility({ reportId, ...variables });
  const newAppliedFilters = cloneDeep(newState.appliedFilters);
  const newPrevAppliedFilters = clone(newState.prevAppliedFilters);

  // Check if filter exists, if not create it using FilterFetchUtility
  let filterConfig = newState.filterConfig?.find((item) => item.ID === filterId && item?.CustomHierarchyId === customHierarchyId);

  if (!filterConfig) {
    // Create filter config if it doesn't exist
    filterConfig = newState.dynamicFilters?.find((item) => item.ID === filterId);
    if (!filterConfig) {
      filterConfig = {
        ID: filterId,
        CustomHierarchyId: customHierarchyId,
      } as ReportResponseModel.IFilterConfig;
    }
  }
  // Use FilterFetchUtility to create proper filter response based on filter type

  if ([FilterType.NumberInteger, FilterType.NumberDecimal].includes(filterConfig.Type)) {
    filterConfig.Operator = OperatorTypes.EQ;
  }
  const filterData = fetchFilter.GetDefaultSingleFilter(filterConfig, filterValue);
  let adjustedFilterValue = filterValue;

  switch (filterConfig.DataType) {
    case ColumnDataTypes.Boolean:
      adjustedFilterValue = [filterValue === 'Yes'];
      break;
    /**
     * Custom hierarchy dimension values in case of date time fields, send the exact time stamp
     * as the corresponding dimension binning type is set to None (as per product requirement).
     * However for date type of filter usually a date range is required, so we need to convert the filter value to a date range.
     * So we are converting the filter value to a date range.
     * For example, if the filter value is "2026-01-01 12:00:00", then we need to convert it to a date range like
     *     Type: IDateRangeTypes.Absolute,
     *     Value: {
     *       From: "2026-01-01T12:00:00", (exact time stamp)
     *       To: "2026-01-01T23:59:59", (till end of the day)
     *     },
     */
    case ColumnDataTypes.DateTime: {
      const [date, time] = filterValue.split(' ');
      adjustedFilterValue = {
        Type: IDateRangeTypes.Absolute,
        Value: {
          From: `${date}T${time}`,
          To: `${date}T${EndOfDayTime}`,
        },
      };
      break;
    }
    default:
      adjustedFilterValue = filterData.FilterResponse;
      break;
  }

  if (adjustedFilterValue?.Value === DimensionNumericNoValue) {
    filterData.FilterResponse = {
      Operator: OperatorTypes.IS_NULL,
      Value: {},
    };
  } else {
    filterData.FilterResponse = adjustedFilterValue;
  }

  if (!isEmpty(filterData.FilterResponse)
    && !isValidArray(filterData.FilterResponse)
    && filterConfig.DataType !== ColumnDataTypes.DateTime
    && ![FilterType.NumberInteger, FilterType.NumberDecimal].includes(filterConfig.Type)
    && adjustedFilterValue !== DimensionNoValue
    && adjustedFilterValue !== DimensionNumericNoValue) { // date time fields are handled separately in the above case.
    filterData.FilterResponse = [filterData.FilterResponse];
  }
  const newAppliedFilter: FilterModel.IFilterResponse = filterData;

  /**
   * We are adding the custom hierarchy filter suffix to differentiate between the explicitly applied filters
   * and the filters added to report while performing the custom hierarchy drilldown.
   */
  newAppliedFilters[`${filterId}${CUSTOM_HIERARCHY_FILTER_SUFFIX}`] = newAppliedFilter;
  // Ensure filter exists in filterConfig with CustomHierarchyId
  const existingFilterIndex = newState.filterConfig?.findIndex((item) => item.ID === filterId && item.CustomHierarchyId === customHierarchyId);
  if (existingFilterIndex !== undefined && existingFilterIndex >= 0) {
    // Update existing filter to set CustomHierarchyId
    newState.filterConfig = [...newState.filterConfig];
    newState.filterConfig[existingFilterIndex] = {
      ...newState.filterConfig[existingFilterIndex],
      CustomHierarchyId: customHierarchyId,
    };
  } else if (newState.filterConfig) {
    // Add filter to filterConfig if it doesn't exist
    newState.filterConfig = [...newState.filterConfig, { ...filterConfig, CustomHierarchyId: customHierarchyId }];
  }
  newState.appliedFilters = newAppliedFilters;
  newState.useChangedFilters = useChangedFilters;
  return newState;
};

// Returns the updated report State on pagination change
// @state - Report State
// @paginationKey - key of pagination object
// @pagination Value - New pagination value
// @isAutodrilldown - boolean to check weather it is auto drilldown or not
export const getStateOnPaginationChange = (
  state: IReportState, paginationKey: string, paginationValue: number,
  isAutodrilldown?: boolean,
) => {
  const newState = state;
  let newPage = clone(newState.page);
  if (isAutodrilldown) {
    newPage = clone(newState.autodrilldownData.page);
  }
  if (paginationKey === PaginationKey.PageSize) {
    newPage.PageIndex = 1;
    newPage.PageSize = paginationValue;
  } else {
    newPage.PageIndex = paginationValue;
  }
  if (isAutodrilldown) {
    newState.autodrilldownData = { ...newState.autodrilldownData, page: newPage };
  } else {
    newState.page = newPage;
  }
  return newState;
};

// Returns the updated report State on sorting change
// @state - Report State
// @columnType - Type of field on which sort is applied
// @order - Order of sort
// @noMultiSort - boolean to check the multiSort
export const getStateOnSortColumn = (
  state: IReportState,
  columnType: draggableItemType,
  columnName: string,
  order: SortingOrderTypes | null, // null implies removal of sort
  multiSort: boolean,
  Id:string,
): IReportState => {
  const newState = state;
  const {
    columns, activeDimensions, sort, pivotSort, savedReportConfig,
  } = newState;
  const [sortState, hasChanged] = sortColumns(
    sort,
    activeDimensions,
    getAppliedMeasures(columns),
    columnType,
    columnName,
    order,
    !newState.reportConfig.IsAugmented && multiSort,
    Id,
    state?.autodrilldownData?.drilldownFields || [],
    false,
    isListReport(savedReportConfig?.Builder[0]?.Type),
    state?.customHierarchyDrilldownData?.columns,
  );
  if (!hasChanged) return state;
  let newPivotSort = pivotSort;
  if (hasPivotColumn(columns)) {
    newPivotSort = sortState?.length ? sortState[0].Direction : pivotSort;
  }
  newState.sort = sortState;
  newState.pivotSort = newPivotSort;
  return newState;
};

// Returns the updated report State on User Drilldown and Custom Hierarchy Drilldown
// @state - Report State
// @filterId - Changed filterId in User Drilldown
// @drilldownValue - drilldown level of the user drilldown level
// @user - clicker user for drilldown
// @customHierarchyId - (Optional) Custom Hierarchy ID for custom hierarchy drilldown
export const getStateOnDrilldownChange = (
  state: IReportState, filterId: string, drilldownValue: any, user: string, varaibles: IVariables, value: any, customHierarchyId?: string,
) => {
  let newState = state;
  const newAppliedFilters = clone(newState.activeFilters);
  const newPrevAppliedFilters = clone(newState.prevAppliedFilters);
  const newAppliedFilter = clone(newAppliedFilters[filterId]);
  newAppliedFilter.Hidden = newPrevAppliedFilters.length ? newState.drilldownPoint.level : user;
  newState.drilldownPoint = {
    filterId,
    level: drilldownValue,
    customHierarchyId, // Store custom hierarchy ID if provided
  };
  newAppliedFilters[filterId] = newAppliedFilter;
  newPrevAppliedFilters.push(newAppliedFilters);
  newState.prevAppliedFilters = newPrevAppliedFilters;
  newState.useChangedFilters = true;
  newState.trackUserDrilldown = true;
  if (newState?.filterConfig && !isEmpty(newState.filterConfig)) {
    const filter = newState.filterConfig.find((item) => item.ID === filterId);
    if (filter) {
      let filterValue = value;
      switch (filter.Type) {
        case FilterType.GroupMultiSelect:
        case FilterType.CustomDefinedMultiSelect:
        case FilterType.LSQMetadataMultiSelect:
        case FilterType.UserMultiSelect:
          if (drilldownValue && !isArray(drilldownValue)) {
            filterValue = [filterValue];
          }
          break;
        default:
          break;
      }
      newState = getStateOnFilterChange(newState, filterId, filterValue, varaibles, true);
    }
  }
  return newState;
};

// Returns the updated report State on Custom Hierarchy Drilldown with stack-based navigation
// @state - Report State
// @filterId - Changed filterId in Custom Hierarchy Drilldown
// @drilldownValue - drilldown level of the custom hierarchy drilldown
// @user - current user
// @variables - user variables
// @value - the value being drilled down on
// @customHierarchyId - Custom Hierarchy ID
// @selectedColumnId - The column ID at this level
// @displayValue - Formatted display value for breadcrumb
export const getStateOnCustomHierarchyDrilldownChange = (
  state: IReportState,
  filterId: string,
  drilldownValue: any,
  user: string,
  variables: IVariables,
  value: any,
  customHierarchyId: string,
  selectedColumnId: string,
  displayValue: string,
) => {
  let newState = state;

  // 1. Store current filter state in prevAppliedFilters stack
  const newAppliedFilters = clone(newState.activeFilters);
  const newPrevAppliedFilters = clone(newState.prevAppliedFilters);
  newPrevAppliedFilters.push(newAppliedFilters);
  newState.prevAppliedFilters = newPrevAppliedFilters;

  // 2. Update drilldownPoint with hierarchy info
  const currentLevel = newState.drilldownPoint?.hierarchyLevel ?? -1;
  newState.drilldownPoint = {
    filterId,
    level: drilldownValue,
    customHierarchyId,
    currentColumnId: selectedColumnId,
    hierarchyLevel: currentLevel + 1,
  };

  // 3. Update customHierarchyDrilldownData with new level
  const newHierarchyChain = [
    ...(newState.customHierarchyDrilldownData?.hierarchyChain || []),
    {
      columnId: selectedColumnId,
      hierarchyId: customHierarchyId,
      clickedValue: value,
      displayValue,
    },
  ];

  // 4. Calculate next columns from hierarchy
  const newColumns = calculateCustomHierarchyDrilldownColumnsFromHierarchies(
    newState.reportConfig?.CustomHierarchies || [],
    newState.customHierarchyDrilldownData?.columns || newState.columns,
    selectedColumnId,
    customHierarchyId,
  );

  newState.customHierarchyDrilldownData = {
    selectedHierarchyId: customHierarchyId,
    hierarchyChain: newHierarchyChain,
    columns: newColumns,
    page: { PageIndex: 1, PageSize: newState.page.PageSize },
    reportData: null,
    sort: [],
    isMultiSort: false,
  };

  // Update the sort state for the report.
  /**
   * If the sort state contains sort config for a column present in the custom hierarchy,
   * the sort config will now contain the entry of the next column in the hierarchy with
   * the same sort config as the parent column.
   */
  const newSortState = newState?.sort || [];
  const doesContainSortConfigForColumnInCustomHierarchy = newSortState.some((sort) => sort.Id === selectedColumnId);
  if (doesContainSortConfigForColumnInCustomHierarchy) {
    const updatedSortState = newSortState.map((sort) => {
      if (sort.Id === selectedColumnId) {
        const nextColumn = newState.customHierarchyDrilldownData.columns.find((col) => col.Props.CustomHierarchyId === customHierarchyId);
        if (nextColumn) {
          return { ...sort, Id: nextColumn.Id };
        }
      }
      return sort;
    });
    newState.sort = updatedSortState;
  }
  // 5. Apply filter changes (delegates all filter logic to getStateOnFilterChangeForCustomHierarchy)
  newState.trackUserDrilldown = false;
  newState = getStateOnFilterChangeForCustomHierarchy(
    newState,
    filterId,
    value,
    variables,
    true, // useChangedFilters
    customHierarchyId,
    drilldownValue,
    user,
  );

  return newState;
};

// Returns the updated report State on Reverse Custom Hierarchy Drilldown (jump to any level)
// @state - Report State
// @index - Target drilldown level index
export const getStateOnReverseCustomHierarchyDrilldown = (
  state: IReportState,
  index: number,
) => {
  const newState = state;
  const customHierarchies = newState.reportConfig?.CustomHierarchies || [];
  // 1. Restore filters from prevAppliedFilters stack (reuse reverse drilldown logic)
  const newPrevAppliedFilters = cloneDeep(newState.prevAppliedFilters).slice(0, index + 1);
  const newAppliedFilters = cloneDeep(newPrevAppliedFilters.pop());

  newState.appliedFilters = newAppliedFilters;
  newState.prevAppliedFilters = newPrevAppliedFilters;

  if (newState.drilldownPoint && newAppliedFilters[newState.drilldownPoint.filterId]) {
    newState.drilldownPoint.level = newAppliedFilters[newState.drilldownPoint.filterId].Hidden;
  }

  // 2. Trim hierarchy chain to target level
  const targetHierarchyChain = newState.customHierarchyDrilldownData.hierarchyChain.slice(0, index);
  const lastColumnIdInTargetHierarchyChain = targetHierarchyChain[targetHierarchyChain.length - 1]?.columnId;
  // 3. Recalculate columns from hierarchy chain
  let currentColumns = newState.columns; // Start from base report columns
  const hierarchies = newState.reportConfig?.CustomHierarchies || [];

  currentColumns = calculateCustomHierarchyDrilldownColumnsFromHierarchies(
    hierarchies,
    currentColumns,
    lastColumnIdInTargetHierarchyChain,
    newState.drilldownPoint?.customHierarchyId,
  );

  // 4. Update customHierarchyDrilldownData
  newState.customHierarchyDrilldownData = {
    ...newState.customHierarchyDrilldownData,
    hierarchyChain: targetHierarchyChain,
    columns: currentColumns,
    page: { PageIndex: 1, PageSize: newState.page.PageSize },
    reportData: null,
  };

  // 5. Update the sort state for the report.
  const newSortState = newState?.sort || [];
  const corresponsingCustomHierarchy = customHierarchies.find((hierarchy) => hierarchy.HierarchyID === newState.drilldownPoint?.customHierarchyId);
  const doesContainSortConfigForColumnInCustomHierarchy = newSortState.some((sort) => corresponsingCustomHierarchy?.ColumnsByID[sort.Id]);
  if (doesContainSortConfigForColumnInCustomHierarchy) {
    const updatedSortState = newSortState.map((sort) => {
      if (corresponsingCustomHierarchy?.ColumnsByID[sort.Id]) {
        const nextColumn = currentColumns.find((col) => col.Props.CustomHierarchyId === newState.drilldownPoint?.customHierarchyId);
        if (nextColumn) {
          return { ...sort, Id: nextColumn.Id };
        }
      }
      return sort;
    });
    newState.sort = updatedSortState;
  }

  // 5. Update drilldownPoint
  if (targetHierarchyChain.length > 0) {
    const lastLevel = targetHierarchyChain[targetHierarchyChain.length - 1];
    newState.drilldownPoint.currentColumnId = lastLevel.columnId;
    newState.drilldownPoint.hierarchyLevel = targetHierarchyChain.length - 1;
  } else {
    // Back to base report - clear custom hierarchy data and remove CustomHierarchyId from filter
    newState.customHierarchyDrilldownData = null;
    newState.drilldownPoint = null;
    // Set flag to indicate user traveled back to main report from custom hierarchy drilldown
    newState.travelBackToMainReportFromCustomHierarchyReport = true;
    /**
     * Remove all the filter config entries added as part of the custom hierarchy drilldown process as there need not be
     * any applied hierarchy config at the main report level.
    */
    newState.filterConfig = [...newState.filterConfig].filter(((item) => !item.CustomHierarchyId));
  }

  newState.page.PageIndex = 1;
  newState.useChangedFilters = true;
  newState.trackUserDrilldown = false;

  return newState;
};

const handleFilterType = (appliedFilter: any, dynamicFilter: ReportResponseModel.IFilterConfig) => {
  switch (dynamicFilter.Type) {
    case FilterType.LSQMetadataSingleSelect:
    case FilterType.CustomDefinedSingleSelect: {
      const filterValue = (appliedFilter?.FilterOptions[0]?.value);
      appliedFilter.FilterResponse = filterValue;
      break;
    }
    default: {
      const defaultFilter = new FilterFetchUtility(null).GetDefaultSingleFilter(dynamicFilter, null);
      appliedFilter.FilterResponse = defaultFilter?.FilterResponse;
      break;
    }
  }
};

// Returns the updated report State on Reverse User Drilldown
// @state - Report State
// @index - Drilldown level
export const getStateOnReverseDrilldown = (state: IReportState, index: number) => {
  const newState = state;
  const newPrevAppliedFilters = cloneDeep(newState.prevAppliedFilters).slice(0, index + 1);
  const newAppliedFilters = cloneDeep(newPrevAppliedFilters.pop());
  const newFilterConfig = newState.reportConfig.Filters;
  const filterIdsInNewAppliedFilters = Object.keys(newAppliedFilters);
  const dynamicFilterIds = filterIdsInNewAppliedFilters.filter((Id) => !!(newFilterConfig.find((filter) => filter.ID !== Id)));
  const newDynamicFilters: ReportResponseModel.IFilterConfig[] = [];
  if (isValidArrayAndNotEmpty(newState.dynamicFilters)) {
    newState.dynamicFilters.forEach((filter:ReportResponseModel.IFilterConfig) => {
      if (filter.ID && isValidArrayAndNotEmpty(dynamicFilterIds) && dynamicFilterIds.includes(filter.ID)) {
        newFilterConfig.push(filter);
        newDynamicFilters.push(filter);
      }
    });
  }

  if (newDynamicFilters && !isEmpty(newDynamicFilters)) {
    newDynamicFilters.forEach((dynamicFilter:ReportResponseModel.IFilterConfig) => {
      if (!newAppliedFilters[dynamicFilter.ID]) {
        const appliedFilter = newState.appliedFilters[dynamicFilter.ID];
        newAppliedFilters[dynamicFilter.ID] = cloneDeep(appliedFilter);
        if (appliedFilter) {
          handleFilterType(appliedFilter, dynamicFilter);
        }
      }
    });
  }
  newState.appliedFilters = newAppliedFilters;
  newState.prevAppliedFilters = newPrevAppliedFilters;
  newState.drilldownPoint.level = newState.appliedFilters[newState.drilldownPoint.filterId].Hidden;
  newState.page.PageIndex = 1;
  newState.useChangedFilters = true;
  newState.trackUserDrilldown = true;
  newState.customHierarchyDrilldownData = null;
  return newState;
};
// Returns the updated report State on Apply filters
// @state - Report State
export const getStateOnApplyingFilters = (state: IReportState) => {
  const newState = state;
  let newFilterConfig = cloneDeep(newState.filterConfig);
  const newAppliedFilters = cloneDeep(newState.appliedFilters);
  if (newFilterConfig && !isEmpty(newFilterConfig)) {
    newFilterConfig = newFilterConfig.filter((config) => !(config.IsDynamic && config.IsRemoved));
  }
  newState.page.PageIndex = 1;
  newState.filterConfig = newFilterConfig;
  newState.appliedFilters = newAppliedFilters;
  newState.activeFilters = newAppliedFilters;
  newState.useChangedFilters = false;

  // set this intermediate state to show loader in report builder component.
  newState.requestProcessing[ReportActionTypes.REPORT_DATA_LOAD_REQUEST] = APIRequestStatus.AboutToLoad;
  return newState;
};

// Returns the updated report State on toolbar menu item change
// @state - Report State
// @index - clicked action fot the tool bar menu item
export const getStateOnSetToolbarMenuItem = (
  state: IReportState, index: number,
) => {
  const newState = state;
  newState.selectedToolbarMenuItem = index;
  return newState;
};

const getZeroAnchor = (desDimension: IDimension) => (desDimension.ZeroAnchor === ZeroAnchorStatus.Disabled
  ? ZeroAnchorStatus.Disabled
  : ZeroAnchorStatus.Enabled);

// Returns the updated report State on dimension order change
// @state - Report State
// @sourceIndex - index of the new dimension
// @status - Dimension applied status
// @dimensionMode - Applied Dimension Mode
// @isSwapEnabled -  boolean to check the weather two dimension swaped
export const getStateOnSetGrouping = (
  state: IReportState,
  sourceIndex: number,
  status: DimensionStatus,
  mode: DimensionMode,
  destinationIndex?: number,
  isSwapEnabled?: boolean,
) => {
  const newState = state;
  let newAppliedDimensions = cloneDeep(newState.appliedDimensions);
  let dimension = newAppliedDimensions[sourceIndex]; // source dimension
  const desDimension = newAppliedDimensions[destinationIndex]; // destination dimension
  const entityGroupStore = newState.entityGroupStore;

  // if date-time field is added in column grouping
  if (status === DimensionStatus.Applied && mode === DimensionMode.ColumnGroup && dimension.DimensionProp?.Props.BinningType === 'NoneExpr') {
    dimension = {
      ...dimension,
      DimensionProp: {
        ...dimension.DimensionProp,
        Props: {
          ...dimension.DimensionProp.Props,
          BinningType: DateBinningFormatters.DATE_YEAR,
        },
      },
      Props: {
        ...dimension.Props,
        Dimension: {
          ...dimension.Props.Dimension,
          DimensionProp: {
            ...dimension.Props.Dimension.DimensionProp,
            Props: {
              ...dimension.Props.Dimension.DimensionProp.Props,
              BinningType: DateBinningFormatters.DATE_YEAR,
            },
          },
        },
      },
    };
  }

  // if swapping is there, then before applying changes to source dimension, we should swap its values in
  // destination dimension.
  if (isSwapEnabled) {
    // if destination dimension is mandatory applied , then it should remain mandatory applied only,
    // if swapped to row grouping
    const desDimStatus = getDestinationDimensionStatus(desDimension, dimension);
    if (desDimension) {
      desDimension.Applied = desDimStatus;
      desDimension.ZeroAnchor = getZeroAnchor(desDimension); // zero values un-applied by default
      desDimension.DimensionMode = dimension?.DimensionMode;
    }
  }
  // making state changes to source dimension (applicable for all cases)
  dimension.Applied = status;
  dimension.ZeroAnchor = dimension.ZeroAnchor === ZeroAnchorStatus.Disabled
    ? ZeroAnchorStatus.Disabled
    : ZeroAnchorStatus.Enabled; // zero values un-applied by default
  dimension.DimensionMode = mode;

  // swapping source dimension with destination dimension in case of swapping true
  if (isSwapEnabled) {
    newAppliedDimensions[destinationIndex] = dimension;
    newAppliedDimensions[sourceIndex] = desDimension;
  }

  // change position of dimension to putBeforeIndex only in case of addition (no swapping)
  if (destinationIndex !== undefined && !isSwapEnabled) {
    newAppliedDimensions.splice(sourceIndex, 1);
    newAppliedDimensions.splice(destinationIndex, 0, dimension);
  }
  // At the time of deletion for entity group Dimension the destination index will be zero
  if (destinationIndex === undefined
    && isEntityGrouped(dimension)
    && dimension?.Id in entityGroupStore) {
    delete entityGroupStore[dimension?.Id];

    // find if there is at least 1 field persisting in the applied dimensions
    const originalDimensionIndex = getOriginalDimensionIndex(newAppliedDimensions, dimension);
    if (originalDimensionIndex > -1) newAppliedDimensions = [...newAppliedDimensions.filter((dim: IDimension) => !(dim?.Id === dimension?.Id || dim?.Props?.ParentDimension === dimension?.Id))];
  }

  newAppliedDimensions = reOrderDimensions(newAppliedDimensions);

  // if pivot is enabled , zero anchor of all applied dimension should set to enabled, if zeroAnchor is applied to send
  // zero anchor disabled to backend call
  const hasColumnGroupings = newAppliedDimensions?.some((dim) => (
    dim?.DimensionMode !== DimensionMode.RowGroup && dim?.Applied !== DimensionStatus.NotApplied
  ));
  if (hasColumnGroupings) {
    newAppliedDimensions?.forEach((dim) => {
      if (dim?.ZeroAnchor === ZeroAnchorStatus.Applied) {
        dim.ZeroAnchor = ZeroAnchorStatus.Enabled;
      }
    });
  }

  /**
   * If a custom column, that had been not been created using custom column expression ( added with custom group button in the earlier features of reports),
   * is being removed, then we need to remove it completely from the applied dimensions list.
   * Else if the column has been created via the Custom columns section, then we can retain the field in the applied dimensions.
   * This is to ensure that the custom groupings created in earlier reports do not affect the custom columns created in the current report feature.
   */

  if (dimension.BuilderConfig?.Entity === FieldEntitiesType.Custom && status === DimensionStatus.NotApplied && destinationIndex === undefined) {
    const existingCustomColumns = cloneDeep(newState.savedReportConfig.Builder?.[0]?.CustomColumns);
    if (!(isValidArrayAndNotEmpty(existingCustomColumns) && existingCustomColumns.some((col) => col.Id === dimension.BuilderConfig.Alias))) {
      newAppliedDimensions = newAppliedDimensions.filter((dim) => dim.Id !== dimension.Id);
    }
    dimension.Applied = DimensionStatus.NotApplied;
  }

  const rowGroupingDimensions = newAppliedDimensions.filter((dim) => dim.DimensionMode === DimensionMode.RowGroup && dim.Applied === DimensionStatus.Applied);
  if (rowGroupingDimensions.length > MaxRowGroupingDimensionsToAllowSubTotals) {
    newState.selectedChartData = {
      ...newState.selectedChartData,
      attributes: {
        ...newState.selectedChartData.attributes,
        showSubTotals: false,
      },
    };
  }
  /**
  * If a custom hierarchy column is being removed, then we need to remove it completely from the applied dimensions list.
  */
  if (isCustomHierarchyColumnOrDimension(dimension) && status === DimensionStatus.NotApplied && destinationIndex === undefined) {
    newAppliedDimensions = newAppliedDimensions.filter((dim) => dim.Id !== dimension.Id);
  }

  // Remove columnId from conditional formatting rules when:
  // 1. A row-grouping dimension is unapplied (status === NotApplied)
  // 2. A dimension is swapped from row-grouping to column-grouping (conditional formatting not supported for column-grouping)
  const shouldRemoveFromConditionalFormatting = status === DimensionStatus.NotApplied
    || (mode === DimensionMode.ColumnGroup);
  if (shouldRemoveFromConditionalFormatting && dimension?.Id) {
    const columnIdToRemove = dimension.Id;
    const currentRules = newState?.ConditionalFormatting?.Rules || [];
    const updatedRules = currentRules?.map((rule) => ({
      ...rule,
      ColumnsIds: rule.ColumnsIds?.filter((colId) => colId !== columnIdToRemove) || [],
    })).filter((rule) => rule.ColumnsIds.length > 0); // Remove rules with no columns
    if (isEmpty(newState.ConditionalFormatting)) {
      newState.ConditionalFormatting = {
        Enabled: true,
        Rules: updatedRules,
      };
    } else {
      newState.ConditionalFormatting.Rules = updatedRules;
    }
  }
  if (status === DimensionStatus.NotApplied && dimension?.Id) {
    const removedDimId = dimension.Id;
    const updatedLinkedReportsConfig = (newState.reportConfig?.Visualization?.LinkedReportsConfig || []).map((cfg) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [removedDimId]: _removed, ...remainingDimensions } = cfg.LinkingConfig.Dimensions;
      return { ...cfg, LinkingConfig: { ...cfg.LinkingConfig, Dimensions: remainingDimensions } };
    });
    newState.reportConfig = {
      ...newState.reportConfig,
      Visualization: {
        ...newState.reportConfig?.Visualization,
        LinkedReportsConfig: updatedLinkedReportsConfig,
      },
    };
  }

  newState.appliedDimensions = newAppliedDimensions;
  return newState;
};

// Returns the updated report State on set measure
// @state - Report State
// @index - new Added measure index
// @status - applied measure status
export const getStateOnMeasureSetState = (state: IReportState, index: number, status: ColumnModes) => {
  if (isEmpty(state.allMeasures) || index < 0 || index >= state.allMeasures.length) {
    return state;
  }
  const newState = state;
  const newMeasures = cloneDeep(newState.allMeasures);
  const measure = newMeasures[index];
  if (isMeasureDynamic(measure)) {
    addDeleteDynamicMeasure(status, newMeasures, index);
  } else if (isMeasureCustom(measure) && status === ColumnModes.NotApplied) {
    newMeasures.splice(index, 1);
  } else {
    measure.Props.Mode = status;
    newMeasures.splice(index, 1);
    newMeasures.push(measure);
  }
  newState.allMeasures = newMeasures;

  const columnIdToRemove = measure.Id;
  const currentRules = newState?.ConditionalFormatting?.Rules || [];
  const updatedRules = currentRules?.map((rule) => ({
    ...rule,
    ColumnsIds: rule.ColumnsIds?.filter((colId) => colId !== columnIdToRemove) || [],
  })).filter((rule) => rule.ColumnsIds.length > 0); // Remove rules with no columns
  if (isEmpty(newState.ConditionalFormatting)) {
    newState.ConditionalFormatting = {
      Enabled: true,
      Rules: updatedRules,
    };
  } else {
    newState.ConditionalFormatting.Rules = updatedRules;
  }

  if (status === ColumnModes.NotApplied) {
    const removedMeasureId = measure.Id;
    const updatedLinkedReportsConfig = (newState.reportConfig?.Visualization?.LinkedReportsConfig || [])
      .map((cfg) => ({
        ...cfg,
        MeasureIdList: cfg.MeasureIdList.filter((id: string) => id !== removedMeasureId),
      }))
      .filter((cfg) => isValidArrayAndNotEmpty(cfg.MeasureIdList));
    newState.reportConfig = {
      ...newState.reportConfig,
      Visualization: {
        ...newState.reportConfig?.Visualization,
        LinkedReportsConfig: updatedLinkedReportsConfig,
      },
    };
  }

  return newState;
};

// Returns the updated measure list after deletion of measure
// @newMeasures - applied measure list
// @index - deleting measure index
// @status - applied measure status
const addDeleteDynamicMeasure = (status: ColumnModes, newMeasures: Array<IColumn>, index:number) => {
  const measure = newMeasures[index];
  if (status === ColumnModes.NotApplied) {
    newMeasures.splice(index, 1);
  } else {
    const updatedName = validateAndAppendCountToName(measure, newMeasures);
    const clonedMeasure = {
      ...measure,
      Id: uuidv4(),
      Name: updatedName,
      Props: {
        ...measure.Props,
        Mode: status,
        Measure: {
          ...measure.Props.Measure,
          Name: updatedName,
        },
      },
      BuilderConfig: {
        ...measure.BuilderConfig,
        DisplayName: updatedName,
      },
    };
    newMeasures.push(clonedMeasure);
  }
};

// Returns the updated report State on update measure
// @state - Report State
// @index - updating measure index
// @newMeasure - new updated measure
export const getStateOnMeasureUpdate = (state: IReportState, index: number, newMeasure: IColumn) => {
  if (isEmpty(state.allMeasures) || index < 0 || index >= state.allMeasures.length) return state;
  const newState = state;
  const newAllMeasures = cloneDeep(newState.allMeasures);
  newAllMeasures[index] = newMeasure;
  newState.allMeasures = newAllMeasures;
  return newState;
};
// Returns the updated report State on measure reorder
// @state - Report State
// @sourceIndex - starting index of measure
// @destinationIndex - final index of measure
export const getStateOnMeasureReorder = (state: IReportState, sourceIndex: number, destinationIndex: number) => {
  if (isEmpty(state.allMeasures) || sourceIndex < 0 || sourceIndex >= state.allMeasures.length
      || destinationIndex < 0 || destinationIndex >= state.allMeasures.length) {
    return state;
  }
  const newState = state;
  const newMeasures = cloneDeep(newState.allMeasures);
  const measure = newMeasures.splice(sourceIndex, 1);
  if (measure.length !== 1) return state;
  newMeasures.splice(destinationIndex, 0, measure[0]);

  newState.allMeasures = newMeasures;
  return newState;
};
// Returns the updated report State on zero Anchor change
// @state - Report State
// @index - index of the dimension for which zero anchor is changed
export const getStateOnSetZeroAnchor = (
  state: IReportState, index: number,
) => {
  if (
    state.appliedDimensions[index].Applied === DimensionStatus.NotApplied
    || state.appliedDimensions[index].ZeroAnchor === ZeroAnchorStatus.Disabled
  ) {
    // no change upon setting inapplicable zero anchors
    return state;
  }

  const newState = state;
  const newAppliedDimensions = cloneDeep(newState.appliedDimensions);

  newState.appliedDimensions.forEach((item, dimIndex) => {
    if (newAppliedDimensions[dimIndex].ZeroAnchor !== ZeroAnchorStatus.Disabled) {
      // clear Applied status of Enabled anchors to enforce selecting only one anchor
      newAppliedDimensions[dimIndex].ZeroAnchor = ZeroAnchorStatus.Enabled;
    }
  });
  newAppliedDimensions[index].ZeroAnchor = ZeroAnchorStatus.Applied;
  newState.appliedDimensions = newAppliedDimensions;
  return newState;
};

const updateFilterConfigResponse = (newState: IReportState, newAppliedFilters: any, partitionColumnFilterDateLimit: number) => {
  newState?.filterConfig?.forEach((filter) => {
    if (filter.IsPartitionColumn && !filter?.IsRemoved) {
      const filterID = filter.ID;
      const filterResponse = newAppliedFilters[filterID]?.FilterResponse;
      if (filterResponse?.Type === IDateRangeTypes.Relative) {
        newAppliedFilters[filterID].FilterResponse = getDatesIfRelativeRangeIsRestricted(filterResponse?.Value, partitionColumnFilterDateLimit);
      } else {
        newAppliedFilters[filterID].FilterResponse = getDatesIfAbsoluteRangeIsRestricted(moment(filterResponse?.Value?.From),
          moment(filterResponse?.Value?.To), partitionColumnFilterDateLimit);
      }
    }
  });
};

const getSortableDimensionName = (sortableDimension: IDimension) => (sortableDimension?.Mode !== ColumnModes.Sortable && sortableDimension?.ReferTo ? sortableDimension?.ReferTo : sortableDimension?.Name);

// Returns the updated report State on apply dimensions
// @state - Report State
export const getStateOnApplyDimensions = (
  state: IReportState,
  partitionColumnFilterDateLimit: number,
) => {
  const newState = state;

  // Resetting the pivotBinningType
  newState.pivotBinningType = '';
  const newActiveDimensions = cloneDeep(newState.appliedDimensions);
  const appliedMeasures = getAppliedMeasures(newState.allMeasures);
  const newAppliedFilters = cloneDeep(newState.activeFilters);
  newState.sort = rebuildSortState(newState.sort, newActiveDimensions, appliedMeasures, newState?.autodrilldownData?.drilldownFields || [], false, false, isListReport(newState.savedReportConfig?.Builder[0]?.Type));

  const pivotColumn = newActiveDimensions.find((dim) => dim.DimensionMode === DimensionMode.ColumnGroup);
  // If there is a pivot column and it has DimensionProp, set pivotBinningType
  if (pivotColumn && pivotColumn.DimensionProp?.DataType === ColumnDataTypes.DateTime && pivotColumn.DimensionProp?.Props) {
    newState.pivotBinningType = pivotColumn.DimensionProp.Props.BinningType;
  }

  // if length of sort array after dimension removal is zero, then adding sorting on 1st measure
  if (newState.sort.length === 0 && appliedMeasures.length > 0) {
    const sortableMeasure = appliedMeasures.find((measure) => measure.Props.AllowDrillDown || measure.Props.Mode === ColumnModes.Sortable);
    const sortableDimension: IDimension = newActiveDimensions.find((dim) => dim.Applied === DimensionStatus.Applied && dim.DimensionMode === DimensionMode.RowGroup && (dim.Mode === ColumnModes.Sortable || dim.ReferTo));
    // As for metafields the Mode comes as zero so for that we pick the ReferTo Value as Name
    const sortableDimensionName = getSortableDimensionName(sortableDimension);
    if (sortableMeasure) {
      newState.sort = [
        {
          Field: sortableMeasure?.Name,
          Direction: SortingOrderTypes.ASC, // keeping it default ascending
          Id: sortableMeasure?.Id,
        },
      ];
    } else if (sortableDimensionName) {
      newState.sort = [
        {
          Field: sortableDimensionName,
          Direction: SortingOrderTypes.ASC, // keeping it default ascending,
          Id: sortableDimension?.Id,
        },
      ];
    } else {
      newState.sort = [];
    }
  }

  newState.activeDimensions = newActiveDimensions;
  // we'll check if string field is applied or not
  const appliedStringField = newActiveDimensions.find((dim) => (dim.DimensionProp?.DataType === ColumnDataTypes.String) && dim?.Applied === DimensionStatus.Applied);
  if (appliedStringField) {
    // will update the response for partition column filter
    updateFilterConfigResponse(newState, newAppliedFilters, partitionColumnFilterDateLimit);
  }
  newState.appliedFilters = newAppliedFilters;
  newState.activeFilters = newAppliedFilters; // apply filters along with applying dimensions if partition column and string field exists
  newState.activeDimensionsLength = getAppliedDimension(newState.appliedDimensions);
  newState.isMultiSort = newState.sort.length > 1;
  // if we're changing the dimensions then HasOthersColumn and mappedMetaFields would depend on the new fetch-report-data call
  if (newState.reportData) {
    newState.reportData.Raw.HasOthersColumn = false;
  }
  newState.mappedMetaFields = null;
  newState.page = { ...newState.page, PageIndex: defaultPageValue.PageIndex };
  return newState;
};

// Returns the updated report State on reorder grid columns
// @state - Report State
// @columns - Array of all the stored columns
// @isAutodrilldown - boolean to check the autodrilldown state
export const getStateOnReorderColumns = (
  state: IReportState, columns: Array<ReportResponseModel.IColumn>,
  isAutodrilldown?: boolean,
) => {
  const newState = state;
  if (isAutodrilldown) {
    newState.autodrilldownData.columns = columns;
    // setting the order of autodrilldownData.drilldownFields when we change the order of columns in the grid directly
    const visibleCols:ReportResponseModel.IColumn[] = columns.filter((dim) => dim?.Props?.Mode !== ColumnModes.Hidden);
    const appliedDrilldownFields:ReportResponseModel.IColumn[] = visibleCols
      ?.map((dim) => newState?.autodrilldownData?.drilldownFields
        ?.find((x) => x?.Id === dim?.Id));
    const unappliedDrilldownFields:ReportResponseModel.IColumn[] = newState?.autodrilldownData?.drilldownFields
      ?.filter((dim) => dim?.Props?.Dimension?.Applied === DimensionStatus.NotApplied);
    newState.autodrilldownData.drilldownFields = appliedDrilldownFields?.concat(unappliedDrilldownFields) || [];
  } else {
    newState.columns = columns;
    // if we remove any measure from values section but has not clicked on apply settings then we need to consider all columns,
    // so getUpdatedMeasure will return the correct state.
    const updatedMeasures = getUpdatedMeasures(newState.allMeasures, columns);
    const allAppliedAndSortedMeasures = reOrderMeasures(updatedMeasures?.filter((mes:IColumn) => isMeasureAndApplied(mes)));
    const allUnappliedMeasures = reOrderMeasures(updatedMeasures?.filter((mes:IColumn) => !isMeasureAndApplied(mes)));
    newState.allMeasures = [...allAppliedAndSortedMeasures, ...allUnappliedMeasures];
  }
  return newState;
};

// Returns the updated report State on toggel zero anchor
// @state - Report State
export const getStateOnToggleZeroAnchor = (
  state: IReportState,
) => {
  const newState = state;
  const newAppliedDimensions = cloneDeep(newState.appliedDimensions);
  newState.appliedDimensions.forEach((item, index) => {
    if (newAppliedDimensions[index].ZeroAnchor !== ZeroAnchorStatus.Disabled) {
      // clear Applied status of Enabled anchors
      newAppliedDimensions[index].ZeroAnchor = ZeroAnchorStatus.Enabled;
    }
  });
  newState.appliedDimensions = newAppliedDimensions;
  newState.zeroValuesEnabled = !newState.zeroValuesEnabled;
  return newState;
};

// Returns the updated report State on doing user filter search
// @state - Report State
// @filterId  - user filter Id
// @searchKey - key on based we filter out the items
export const getStateOnSearchDropdownOptions = (
  state: IReportState, filterId: string, searchKey: string,
) => {
  const newState = state;
  const filter = newState.filterConfig.find((config) => config.ID === filterId);
  const newAppliedFilters = cloneDeep(newState.appliedFilters);
  if (filter) {
    newAppliedFilters[filterId].OptionsMeta.SearchKey = searchKey;
    newAppliedFilters[filterId].OptionsMeta.Page.PageIndex = 0;
    newAppliedFilters[filterId].FilterOptions = [];
  }
  newState.appliedFilters = newAppliedFilters;
  newState.useChangedFilters = false;
  return newState;
};

// Returns the updated report State on scrolling user filter.
// @state - Report State
// @filterId  - user filter Id
export const getStateOnLoadMoreDropdownOptions = (
  state: IReportState, filterId: string,
) => {
  const newState = state;
  const filter = newState.filterConfig.find((config) => config.ID === filterId);
  const newAppliedFilters = cloneDeep(newState.appliedFilters);
  if (filter) {
    newAppliedFilters[filterId].OptionsMeta.Page.PageIndex += 1;
  }
  newState.appliedFilters = newAppliedFilters;
  newState.useChangedFilters = false;
  return newState;
};

// Returns the updated report State on Adding the dynamic filter.
// @state - Report State
// @index  - index of newely added filter
export const getStateOnAddDynamicFilter = (
  state: IReportState, index: number,
) => {
  const newState = state;
  const stateClone = cloneDeep(newState);
  let newFilterConfig = cloneDeep(newState.filterConfig);
  const newAppliedFilters = cloneDeep(newState.appliedFilters);
  const dynamicFilter = cloneDeep(newState.dynamicFilters)[index];
  const userMultiSelectFilterData = newFilterConfig.find((config:ReportResponseModel.IFilterConfig) => config.Type === FilterType.UserMultiSelect);
  newFilterConfig = newFilterConfig.filter((config: ReportResponseModel.IFilterConfig) => (config.CustomHierarchyId || config.ID !== dynamicFilter?.ID));
  dynamicFilter.IsPrimaryUserFilter = false;
  newFilterConfig.push(dynamicFilter);
  const reportId = stateClone.reportId;
  newAppliedFilters[dynamicFilter.ID] = new FilterFetchUtility({ reportId }).GetDefaultSingleFilter(dynamicFilter, null);
  if (dynamicFilter?.Entity === FieldEntitiesType.User && userMultiSelectFilterData) {
    newState.isUsersModified = true;
    newAppliedFilters[userMultiSelectFilterData.ID] = new FilterFetchUtility({ reportId }).GetDefaultSingleFilter(userMultiSelectFilterData, null);
  }
  newState.filterConfig = newFilterConfig;
  newState.appliedFilters = newAppliedFilters;
  if (!isEmpty(newState.customHierarchyDrilldownData)) {
    const prevAppliedFilters = cloneDeep(newState.prevAppliedFilters);
    prevAppliedFilters[prevAppliedFilters?.length - 1] = newAppliedFilters;
    newState.prevAppliedFilters = prevAppliedFilters;
  }
  newState.useChangedFilters = false;
  return newState;
};

// Returns the updated report State on removing the dynamic filter.
// @state - Report State
// @filterId  - Id of deleted filter
export const getStateOnDeleteDynamicFilter = (
  state: IReportState, filterId: string,
) => {
  const newState = state;
  const newFilterConfig = cloneDeep(newState.filterConfig);
  const newAppliedFilters = cloneDeep(newState.appliedFilters);
  const index = newFilterConfig.findIndex((config) => config.ID === filterId);

  const appliedDimensions = state.appliedDimensions;
  const deletedFilter = index !== -1 ? newFilterConfig[index] : null;
  if (deletedFilter?.Type === FilterType.DateRange && deletedFilter?.LinkedDimensionId && isValidArrayAndNotEmpty(appliedDimensions)) {
    const isLinkedDimAppliedIndex = appliedDimensions.findIndex((dim: IDimension) => dim.Applied === DimensionStatus.Applied && truncateUUID(deletedFilter?.LinkedDimensionId) === dim.Id);
    if (isLinkedDimAppliedIndex !== -1) {
      appliedDimensions[isLinkedDimAppliedIndex] = {
        ...appliedDimensions[isLinkedDimAppliedIndex],
        RangeFill: 0,
        Props: {
          ...appliedDimensions[isLinkedDimAppliedIndex]?.Props,
          Dimension: {
            ...appliedDimensions[isLinkedDimAppliedIndex]?.Props?.Dimension,
            RangeFill: 0,
          },
        },
      };
    }
    state.appliedDimensions = appliedDimensions;
  }

  const userMultiSelectFilterData = newFilterConfig.find((config) => config.Type === FilterType.UserMultiSelect);
  if (index !== -1) {
    newFilterConfig[index].IsRemoved = true;
    if (newFilterConfig[index]?.Entity === FieldEntitiesType.User && userMultiSelectFilterData) {
      newState.isUsersModified = true;
      newAppliedFilters[userMultiSelectFilterData.ID] = new FilterFetchUtility(null).GetDefaultSingleFilter(userMultiSelectFilterData, null);
    }
    // Deleting a custom filter: tombstone the custom filter entry and its non-AlwaysEnabled
    // constituents (IsRemoved=true) instead of splicing them out, mirroring the standalone
    // delete path. isFilterChanged iterates filterConfig and relies on the XOR between
    // activeFilters and appliedFilters to flag a deletion as a dirty change — splicing
    // entries out leaves the loop blind to them and Apply stays disabled.
    // getStateOnApplyingFilters drops `IsDynamic && IsRemoved` entries on next Apply.
    // AssociatedCustomFilterId is cleared on every freed constituent so no surviving
    // entry references the deleted custom filter. AlwaysEnabled constituents stay live
    // (no tombstone) and continue as standalone filters with a re-initialised value.
    if (newFilterConfig[index]?.IsCustomFilter) {
      const linkedIds = (newFilterConfig[index]?.LinkedFilters || []).map((lf) => lf.FilterId);
      const constituentIds = new Set(linkedIds);
      newFilterConfig.forEach((config) => {
        if (config.AssociatedCustomFilterId === filterId || constituentIds.has(config.ID)) {
          delete config.AssociatedCustomFilterId;
          if (config.AlwaysEnabled) {
            newAppliedFilters[config.ID] = new FilterFetchUtility(null).GetDefaultSingleFilter(config, null);
          } else {
            config.IsRemoved = true;
            delete newAppliedFilters[config.ID];
          }
        }
      });
    }
  }
  delete newAppliedFilters[filterId];
  newState.appliedFilters = newAppliedFilters;

  // Purge stale references to the deleted custom filter from four places:
  //
  //   1. reportConfig.Filters and dynamicFilters — REMOVE the stamped entries entirely.
  //      dynamicFilters is built as [...reportConfig.Filters(IsAdditional), ...dimensionsApi]
  //      (report-store.reducer.ts:737), so the stamped reportConfig entries shadow the
  //      dimensions-API entries by ID, keeping their saved-config NsDisplayName and forcing
  //      the freed constituent into a separate accordion section in the Add Filters tray.
  //      Removing them lets the API-sourced version (empty NsDisplayName) win.
  //
  //   2. savedReportConfig.Builder/SubBuilders FilterGroup — STRIP the stale
  //      AssociatedCustomFilterId in place (don't remove the entry). The save flow
  //      (getUpdatedReportConfig → getSavePayloadBuilderFilters → updateReportTemplateFilterBuilder)
  //      walks these groups and drops items whose ACF references a CF no longer in
  //      filterConfig. Freed AlwaysEnabled constituents stay live in filterConfig as
  //      standalones and must reach the save payload via this saved structure — without
  //      stripping the stale ACF, the skip would drop them.
  //
  //   3. savedReportConfig.CustomFilterDefinitions — REMOVE the entry for the deleted CF.
  //      getUpdatedReportConfig builds the save payload by spreading savedReportConfig,
  //      with no override for CustomFilterDefinitions. Without this cleanup, the deleted
  //      CF rides through to the save payload as a stale definition.
  //
  // Entries pointing at other (still-active) custom filters are untouched.
  const isStaleAssociation = (f: { AssociatedCustomFilterId?: string }) => f.AssociatedCustomFilterId === filterId;
  const stripStaleAssociation = <T extends { AssociatedCustomFilterId?: string }>(f: T): T => {
    if (!isStaleAssociation(f)) return f;
    const cleaned = { ...f };
    delete cleaned.AssociatedCustomFilterId;
    return cleaned;
  };
  if (isValidArrayAndNotEmpty(newState.reportConfig?.Filters)) {
    newState.reportConfig = {
      ...newState.reportConfig,
      Filters: newState.reportConfig.Filters.filter((f) => !isStaleAssociation(f)),
    };
  }
  if (isValidArrayAndNotEmpty(newState.dynamicFilters)) {
    newState.dynamicFilters = newState.dynamicFilters.filter((f) => !isStaleAssociation(f));
  }
  if (newState.savedReportConfig) {
    const stripFromBuilderFilter = (bf?: IBuilderFilter): IBuilderFilter | undefined => (
      bf
        ? {
          ...bf,
          FilterGroup: bf.FilterGroup?.map(stripStaleAssociation),
          SubFilters: bf.SubFilters?.map(stripFromBuilderFilter),
        }
        : bf
    );
    newState.savedReportConfig = {
      ...newState.savedReportConfig,
      Builder: newState.savedReportConfig.Builder?.map((b) => ({ ...b, Filter: stripFromBuilderFilter(b.Filter) })),
      SubBuilders: newState.savedReportConfig.SubBuilders?.map((b) => ({ ...b, Filter: stripFromBuilderFilter(b.Filter) })),
      CustomFilterDefinitions: newState.savedReportConfig.CustomFilterDefinitions?.filter((d) => d.ID !== filterId),
    };
  }

  const updatedLinkedReportsConfig = (newState.reportConfig?.Visualization?.LinkedReportsConfig || []).map((cfg) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [filterId]: _removed, ...remainingFilters } = cfg.LinkingConfig.Filters;
    return { ...cfg, LinkingConfig: { ...cfg.LinkingConfig, Filters: remainingFilters } };
  });
  newState.reportConfig = {
    ...newState.reportConfig,
    Visualization: {
      ...newState.reportConfig?.Visualization,
      LinkedReportsConfig: updatedLinkedReportsConfig,
    },
  };

  if (!isEmpty(newState.customHierarchyDrilldownData)) {
    const prevAppliedFilters = cloneDeep(newState.prevAppliedFilters);
    prevAppliedFilters[prevAppliedFilters?.length - 1] = newAppliedFilters;
  }
  newState.useChangedFilters = false;
  newState.filterConfig = newFilterConfig;
  return newState;
};

// Returns the updated report State on trigger autodrilldown.
// @state - Report State
export const getStateOnAutoDrilldown = (
  state: IReportState, selectedMeasure: string, selectedRowData: ObjModel.Obj, measureFieldAlias: string,
  rowPinnedClicked: boolean, clickedOnColumnTotal: boolean, clickedOnRowTotal: boolean,
) => {
  const newState = cloneDeep(state);
  if (selectedMeasure) {
    newState.autodrilldownData = {
      selectedMeasure,
      selectedRowData,
      page: {
        PageSize: 25,
        PageIndex: 1,
      },
      columns: [],
      sort: [],
      isMultiSort: false,
      reportData: null,
      drilldownFields: null,
      measureFieldAlias,
      rowPinnedClicked,
      clickedOnColumnTotal,
      clickedOnRowTotal,
    };
  } else {
    newState.autodrilldownData = null;
    newState.autoDrilldownStateID = '';
    newState.autoDrilldownDataV2 = null;
  }

  return newState;
};

// Function is called whenever there is change in dimensions. Update dimension and rearrange columns
// based on current order of dimensions. Rearrange the dimension's referer column next to parent column
// (has dimension and ReferTo key) in columns array.
// param columns: array of columns in report config.
// param activeDimensions: array of active(used to fetch report) dimensions.
// returns: array of columns with updated dimension.
export const getColumnsForActiveDimensions = (
  columns: Array<ReportResponseModel.IColumn>, activeDimensions: Array<ReportReduxModel.IDimension>,
  measures: Array<ReportResponseModel.IColumn>, dynamicDimensions: Array<ReportResponseModel.IColumn>,
  entityGroupStore: Record<string, string>, entityGroupOptionsMap: Record<string, IDimensionMetaField[]>,
  blockDrilldown?: boolean,
): Array<ReportResponseModel.IColumn> => {
  let currColumns = cloneDeep(columns);
  let newColumns = [] as Array<ReportResponseModel.IColumn>;
  activeDimensions.forEach((item) => {
    const column = currColumns.find((col) => areDimensionsEqual(item, col));
    const refererCol = item.ReferTo
      ? currColumns.find((col) => col.Id === item.ReferTo)
      : null;
    if (column && isValidObjectAndNotEmpty(column?.Props?.Dimension)) {
      if (column?.BuilderConfig?.IsDynamicField && column?.BuilderConfig?.Alias?.length && item.Applied !== DimensionStatus.Applied) {
        currColumns = currColumns.filter((col) => !areDimensionsEqual(col, column));
        // Filter out the metafields
        currColumns = currColumns.filter((col) => item?.BuilderConfig.IsDynamicField && item.Applied === DimensionStatus.NotApplied && item.ReferTo !== col?.Id);
        return;
      }
      const dimension = cloneDeep(column.Props.Dimension);
      dimension.Applied = item.Applied;
      dimension.Orderable = item.Orderable;
      dimension.ZeroAnchor = item.ZeroAnchor;
      dimension.DimensionMode = item.DimensionMode;
      dimension.DimensionProp = item.DimensionProp;
      column.Props.Dimension = dimension;
      column.Props.Mode = item.Mode;
      column.Id = item.Id;
      column.Props.Namespace = item?.Props?.Namespace;
      column.Props.IsMetaFieldSelector = item?.Props?.IsMetaFieldSelector;
      column.Props.MetaFieldSelectorDisplayName = item?.Props?.MetaFieldSelectorDisplayName;
      column.Props.MetaFieldSelectorID = item?.Props?.MetaFieldSelectorID;
      column.Props.MetaFieldSelectorSchemaNames = item?.Props?.MetaFieldSelectorSchemaNames;
      column.Props.IsColGroupingAllowed = item?.Props?.IsColGroupingAllowed;
      column.Props.IsSourceCustomColumn = item?.Props?.IsSourceCustomColumn || false;
      newColumns.push(column);
      // Filter out the columns which doesn't matches the with the active dimensions
      currColumns = currColumns.filter((col) => !areDimensionsEqual(item, col));
      if (refererCol) {
        newColumns.push(refererCol);
        currColumns = currColumns.filter((col) => col.Id !== item.ReferTo);
      }
    } else if (item.Applied === DimensionStatus.Applied) {
      const dimension = cloneDeep(item);
      dimension.Applied = item.Applied;
      dimension.Orderable = item.Orderable;
      dimension.ZeroAnchor = item.ZeroAnchor;
      dimension.DimensionMode = item.DimensionMode;
      const newColumn = {
        Name: dimension.Name,
        Id: dimension.Id,
        BuilderConfig: dimension.BuilderConfig,
      } as IColumn;
      delete dimension.BuilderConfig;
      newColumn.Props = {
        Dimension: dimension,
        Action: null,
        Converter: null,
        Formatter: null,
        IsMeasure: false,
        Mode: item.Mode,
        ReferTo: item.ReferTo,
        IsMasked: item.IsMasked,
        IsUserDynamicField: item.IsUserDynamicField,
        Namespace: item?.Props?.Namespace,
        IsMetaFieldSelector: item?.Props?.IsMetaFieldSelector,
        MetaFieldSelectorDisplayName: item?.Props?.MetaFieldSelectorDisplayName,
        MetaFieldSelectorID: item?.Props?.MetaFieldSelectorID,
        MetaFieldSelectorSchemaNames: item?.Props?.MetaFieldSelectorSchemaNames,
        IsColGroupingAllowed: item?.Props?.IsColGroupingAllowed,
        IsSourceCustomColumn: item?.Props?.IsSourceCustomColumn || false,
      };
      newColumns.push(newColumn);
      if (item?.BuilderConfig.IsDynamicField && item.ReferTo) {
        const referredDynamicField = dynamicDimensions.find((dim) => dim?.Id === item?.ReferTo);
        if (referredDynamicField) {
          newColumns.push(referredDynamicField);
        }
      }
    }
  });
  newColumns = processEntityGroupDimensions(newColumns, activeDimensions, entityGroupStore, entityGroupOptionsMap);
  return newColumns.concat(
    measures.filter((m) => isMeasure(m) && (isMeasureAndApplied(m) || isMeasureCustom(m) || !m.Props.Measure.Expression)),
  );
};

// Returns the Applied Dimensions
// @input - Array of all the dimensions
export const getAppliedDimension = (input: ReportReduxModel.IDimension[]) => {
  const appliedvalue = input.filter((item: ReportReduxModel.IDimension) => item.Applied !== DimensionStatus.NotApplied);
  const rowGroupColumns = appliedvalue.filter((item) => item.DimensionMode === DimensionMode.RowGroup);
  return rowGroupColumns.length;
};

// Returns the updated report State on dynamic dimension API success.
// @state - Report State
// @dynamic Dimensions - list of new dimensions
// @selectedMeasure - value to check the drilldown state
export const getDynamicDimensionOnDynamicDimensionLoadSuccess = (
  state: IReportState,
  dynamicDimensions: Array<ReportResponseModel.IColumn>,
  selectedMeasure?: MeasureIdentifier,
) => {
  if (!selectedMeasure) {
    return handleDynamicDimensionsWithoutSelectedMeasure(state, dynamicDimensions);
  }
  return handleDynamicDimensionsWithSelectedMeasure(state, dynamicDimensions);
};

// Returns the updated report State on dynamic dimension API success in report page flow.
// @state - Report State
// @dynamic Dimensions - list of new dimensions
// @selectedMeasure - value to check the drilldown state
const handleDynamicDimensionsWithoutSelectedMeasure = (
  state: IReportState,
  dynamicDimensions: Array<ReportResponseModel.IColumn>,
) => {
  const newState = state;
  const newDynamicDimensions = cloneDeep(dynamicDimensions);
  let allDimensions = reOrderDimensions(newState.appliedDimensions);

  const castedDimensions = getCastedDimensions(newState, newDynamicDimensions) || [];
  // keep report dimensions at the top for better UX, separate from dynamic dimensions
  allDimensions = [...allDimensions, ...castedDimensions];

  newState.dynamicDimensions = newDynamicDimensions || [];
  newState.appliedDimensions = allDimensions; // no meaning of applied and active dimensions for autodrilldown
  newState.activeDimensions = allDimensions;
  newState.requestProcessing[ReportActionTypes.REPORT_DYNAMIC_DIMENSIONS_LOAD_REQUEST] = APIRequestStatus.Success;
  return newState;
};

// converts and returns updated  dimensions array based on the list of dimensions.
// @state - Report State
// @dynamic Dimensions - list of new dimensions
const getCastedDimensions = (
  state: IReportState,
  newDynamicDimensions: Array<ReportResponseModel.IColumn>,
) => newDynamicDimensions
  ?.filter(
    (newDim) => state.appliedDimensions.findIndex(
      (appliedDim: IDimension) => getEntityKey(appliedDim) === getEntityKey(newDim),
    ) === -1,
  )
  ?.map((dim: IColumn) => ({
    Applied: DimensionStatus.NotApplied,
    Orderable: dim.Props?.Dimension?.Orderable,
    ZeroAnchor: dim.Props?.Dimension?.ZeroAnchor,
    DimensionMode: dim.Props?.Dimension?.DimensionMode,
    BuilderConfig: dim.BuilderConfig,
    Name: dim.Name,
    Id: dim.Id,
    IsMasked: dim.Props.IsMasked,
    DimensionProp: dim.Props?.Dimension?.DimensionProp,
    Mode: dim.Props?.Mode,
    ReferTo: dim.Props?.ReferTo,
    IsUserDynamicField: dim?.Props?.IsUserDynamicField,
    Props: dim?.Props,
    IsUserDataType: dim?.BuilderConfig?.IsUserDataType,
  } as IDimension));

// Returns the updated report State on dynamic dimension API success in drilldown flow for selected measure.
// @state - Report State
// @dynamic Dimensions - list of new dimensions
// @selectedMeasure - value to check the drilldown state
const handleDynamicDimensionsWithSelectedMeasure = (
  state: IReportState,
  dynamicDimensions: Array<ReportResponseModel.IColumn>,
) => {
  const newState = state;
  const newDynamicDimensions = dynamicDimensions || [];

  if (newState?.autodrilldownData) {
    newState.autodrilldownData.drilldownFields = newDynamicDimensions?.filter(
      (dim: ReportResponseModel.IColumn) => dim.Props.Mode !== ColumnModes.Hidden,
    ) || [];
  }

  const visibleCols = getVisibleCols(newState);
  const { appliedDrilldownFields, unappliedDrilldownFields } = getAppliedAndUnappliedDrilldownFields(newState, visibleCols);

  // ordering appliedDrilldownFields according to the visualisation columns from fetch-drilldown-report API
  const orderedAppliedDrilldownFields = getOrderedAppliedDrilldownFields(newState, appliedDrilldownFields);

  // TODO SIERA-3098 - we are not getting all column alias in auto-drilldown-additional fileds, Need to check the data issue which is happening only for few users. Addting quick filter fix
  if (newState?.autodrilldownData) {
    newState.autodrilldownData.drilldownFields = orderedAppliedDrilldownFields?.concat(unappliedDrilldownFields)?.filter((dim: IColumn) => dim !== undefined) || [];
  }

  newState.requestProcessing[ReportActionTypes.REPORT_DYNAMIC_DIMENSIONS_LOAD_REQUEST] = APIRequestStatus.Success;
  return newState;
};

const getVisibleCols = (state: IReportState) : Set<string> => {
  const visibleCols = new Set<string>();
  state?.autodrilldownData?.columns?.forEach((dim: ReportResponseModel.IColumn) => {
    if (dim.Props.Mode !== ColumnModes.Hidden && dim?.BuilderConfig?.Alias) {
      visibleCols.add(getUniqIdentiferForDrilldownCols(dim));
    }
  });
  return visibleCols;
};

const getAppliedAndUnappliedDrilldownFields = (
  state: IReportState,
  visibleCols: Set<string>,
) => {
  const appliedDrilldownFields: ReportResponseModel.IColumn[] = [];
  const unappliedDrilldownFields: ReportResponseModel.IColumn[] = [];
  if (!isValidArrayAndNotEmpty(state?.autodrilldownData?.drilldownFields)) {
    return { appliedDrilldownFields, unappliedDrilldownFields };
  }

  state?.autodrilldownData?.drilldownFields?.forEach((dim) => {
    const newDim = cloneDeep(dim);
    newDim.Props.isDrilldownColumn = true;
    if (dim?.Props?.Dimension) {
      if (visibleCols.has(getUniqIdentiferForDrilldownCols(dim))) {
        newDim.Props.Dimension.Applied = DimensionStatus.Applied;
        const updatedDim = updateVisibleColumnProperties(newDim, state);
        appliedDrilldownFields.push(updatedDim);
      } else {
        newDim.Props.Dimension.Applied = DimensionStatus.NotApplied;
        unappliedDrilldownFields.push(newDim);
      }
    }
  });

  return { appliedDrilldownFields, unappliedDrilldownFields };
};
// finds and Returns the column with updated properties.
// @state - Report State
// @newDim - column
const updateVisibleColumnProperties = (
  newDim: ReportResponseModel.IColumn,
  state: IReportState,
) => {
  const visibleColumn = findVisibleColumn(state, newDim);
  if (visibleColumn) {
    newDim.Name = visibleColumn.Name;
    newDim.Props.Formatter = visibleColumn?.Props?.Formatter || null;
    newDim.BuilderConfig.DisplayName = visibleColumn.BuilderConfig.DisplayName;
    newDim.BuilderConfig.IsDynamicField = visibleColumn.BuilderConfig.IsDynamicField;
  }
  return newDim;
};

const findVisibleColumn = (
  state: IReportState,
  dim: ReportResponseModel.IColumn,
): ReportResponseModel.IColumn | undefined => {
  if (!isValidArrayAndNotEmpty(state?.autodrilldownData?.columns)) {
    return undefined;
  }
  return state.autodrilldownData.columns.find(
    (col: IColumn) => getUniqIdentiferForDrilldownCols(col) === getUniqIdentiferForDrilldownCols(dim),
  );
};

const getOrderedAppliedDrilldownFields = (
  state: IReportState,
  appliedDrilldownFields: IColumn[],
) => {
  const orderedAppliedDrilldownFields: IColumn[] = [];
  if (isValidArrayAndNotEmpty(state.autodrilldownData?.columns)) {
    state.autodrilldownData?.columns?.forEach((element) => {
      const item = appliedDrilldownFields.find((field) => field?.BuilderConfig?.Alias === element?.BuilderConfig?.Alias
  && field?.BuilderConfig?.Entity === element?.BuilderConfig?.Entity
  && (!element?.BuilderConfig?.JoinID
   || field?.BuilderConfig?.JoinID === element.BuilderConfig.JoinID));
      const updatedItem = { // overriding meta selection properties
        ...item,
        Id: element.Id,
        Name: element.Name,
        Props: {
          ...item?.Props,
          MetaFieldSelectorDisplayName: element?.Props?.MetaFieldSelectorDisplayName,
          MetaFieldSelectorID: element?.Props?.MetaFieldSelectorID,
          MetaFieldSelectorSchemaNames: element?.Props?.MetaFieldSelectorSchemaNames,
          IsMetaFieldSelector: element?.Props?.IsMetaFieldSelector,
          IsDimensionMetaField: element.Props.IsDimensionMetaField,
        },
      };
      return element?.Props?.Mode !== ColumnModes.Hidden
      && orderedAppliedDrilldownFields.push(element?.Props?.IsMetaFieldSelector ? updatedItem : item);
    });
  }
  return orderedAppliedDrilldownFields;
};

// Returns the updated report State on autodrilldown API success.
// @state - Report State
// @reportData - autodrilldown report data
export const getStateOnAutodrilldownReportDataLoadSuccess = (
  state: IReportState, reportData: ReportResponseModel.IReportData,
) => {
  const newState = state;
  const newAutodrilldownData = newState.autodrilldownData;
  newState.requestProcessing[ReportActionTypes.AUTODRILLDOWN_REPORT_DATA_LOAD_REQUEST] = APIRequestStatus.Success;
  if (!isEmpty(reportData)) {
    newState.selectedToolbarMenuItem = -1;
    newAutodrilldownData.columns = reportData.Visualization?.Columns.map((col) => { col.Props.isDrilldownColumn = true; return col; });
    newAutodrilldownData.sort = reportData.Visualization?.DefaultSort;
    newAutodrilldownData.isMultiSort = reportData.Visualization?.DefaultSort?.length > 1;
    newAutodrilldownData.reportData = reportData;
    newAutodrilldownData.page.PageSize = reportData.Visualization?.DefaultPage?.PageSize ? reportData.Visualization.DefaultPage.PageSize : defaultPageValue.PageSize;
    newState.autodrilldownData = newAutodrilldownData;
  }
  const newStoreConfig = generateEntityStore(newState.autodrilldownData.columns, state.entityGroupStore);
  newState.entityGroupStore = {
    ...newState.entityGroupStore,
    ...newStoreConfig,
  };
  return newState;
};
// Returns the updated report State on redirected autodrilldown API success.
// @state - Report State
// @reportData - autodrilldown report data
export const getStateOnAutoDrilldownV2ReportDataLoadSuccess = (state: IReportState, data: ReportReduxModel.IAutoDrilldownV2Data) => {
  const newState = state;
  newState.autoDrilldownDataV2 = data;
  newState.autoDrilldownDataV2.DrilldownColumns = data?.DrilldownColumns?.map((col) => { col.Props.isDrilldownColumn = true; return col; });
  newState.showReport = true;
  return newState;
};

// Returns the updated report State on dimension binning change
// @state - Report State
// @index - index of the dimension where the binning is changed
// @value - value of new binning
export const getStateOnSetBinning = (state: IReportState, index: any, value: string) => {
  const newState = state;
  const newAppliedDimensions = cloneDeep(newState.appliedDimensions);
  const newAppliedDimension = newAppliedDimensions[index];
  newAppliedDimension.DimensionProp.Props.BinningType = value;
  newAppliedDimension.Props.Dimension.DimensionProp.Props.BinningType = value;
  if (newAppliedDimension.ZeroAnchor !== ZeroAnchorStatus.Disabled) {
    // unapply zero values for all binning types except Hour of the Day(backend is not compatible)
    if (newAppliedDimension.DimensionProp && newAppliedDimension.DimensionProp?.Props?.BinningType !== DateBinningFormatters.HOUROFTHEDAY) {
      newAppliedDimension.ZeroAnchor = ZeroAnchorStatus.Enabled;
    }
  }
  newAppliedDimensions[index] = newAppliedDimension;
  newState.appliedDimensions = newAppliedDimensions;
  return newState;
};

// Returns the updated report State on drilldown dimension change
// @state - Report State
// @sourceIndex - index of the new dimension
// @status - Dimension applied status
// @dimensionMode - Applied Dimension Mode
// @isSwapEnabled -  boolean to check the weather two dimension swaped
export const getStateOnSetGroupingDrilldown = (
  state: IReportState,
  sourceIndex: number,
  status: DimensionStatus,
  destinationIndex?: number,
  isSwapEnabled?: boolean,
) => {
  const newState = state;
  const entityGroupStore = newState.entityGroupStore;
  let newDrilldownFields = cloneDeep(newState.autodrilldownData.drilldownFields);
  const srcDimension = newDrilldownFields[sourceIndex]; // source dimension
  const desDimension = newDrilldownFields[destinationIndex]; // destination dimension
  srcDimension.Props.Dimension.Applied = status;
  if (isSwapEnabled) {
    newDrilldownFields[destinationIndex] = srcDimension;
    newDrilldownFields[sourceIndex] = desDimension;
  }

  // in case of addition
  if (destinationIndex !== undefined && !isSwapEnabled) {
    newDrilldownFields = newDrilldownFields.filter((dim) => !areDimensionsEqual(dim, srcDimension));
    newDrilldownFields.splice(destinationIndex, 0, srcDimension);
  }
  // At the time of deletion for entity group Dimension the destination index will be zero
  if (destinationIndex === undefined
      && isEntityGrouped(srcDimension)
      && srcDimension?.Id in entityGroupStore) {
    delete entityGroupStore[srcDimension?.Id];
    // find if there is at least 1 field persisting in the applied dimensions
    const originalDimensionIndex = getOriginalDimensionIndex(newDrilldownFields, srcDimension, true);
    if (originalDimensionIndex > -1) newDrilldownFields = [...newDrilldownFields.filter((dim: IColumn) => !(dim?.Id === srcDimension?.Id))];
  }

  // in case of removal just reorder
  newDrilldownFields = reorderDrilldownFields(newDrilldownFields);
  newState.autodrilldownData.drilldownFields = newDrilldownFields || [];
  newState.entityGroupStore = entityGroupStore;
  newState.autodrilldownData.sort = rebuildSortState(newState.autodrilldownData.sort, newState.activeDimensions, getAppliedMeasures(newState.columns), newState?.autodrilldownData?.drilldownFields, newState?.autodrilldownData?.sort?.length <= 1, true);
  return newState;
};

// Returns the updated drilldown field order on reorder drilldown dimension
// @drilldownFields - updated drilldown fields
export const reorderDrilldownFields = (drilldownFields: IColumn[]): IColumn[] => {
  const groupedDimensions = drilldownFields.filter((dim) => (
    dim?.Props?.Dimension?.Applied === DimensionStatus.Applied
  ));
  const availableDrilldownFields = drilldownFields?.filter((dim) => dim?.Props?.Dimension?.Applied === DimensionStatus.NotApplied) || [];
  return [...groupedDimensions, ...availableDrilldownFields];
};

// Returns the updated report state on date Refresh success
// @jobDetails - jobinfo and status
// @ispolling - boolean to check weather the polling is required
export const dataRefreshDetailsLoadSuccess = (state: IReportState, jobDetails: DataRefreshEntityTypes, isPolling?: boolean) => {
  const newState = state;
  const newJobObject = newState.jobObject;
  const newJobsArray = [] as IJob[];
  const jobTypes = isValidObjectAndNotEmpty(jobDetails) ? Object.keys(jobDetails).map((key) => key) : [];
  jobTypes?.forEach((jobType, index) => {
    // filter job type
    const today = moment();
    const dataRefreshed = jobDetails?.[jobType]?.[JobTypes.Load]?.LastRefreshed || 0;
    const newRefreshTime = today.subtract(dataRefreshed, 'minutes').toDate();
    const prevDataRefreshTime = state?.jobObject?.jobsArray[index]?.Details?.LastRefreshed || dataRefreshed;
    const isRefreshRequiredAlreadySet = state?.jobObject?.jobsArray[index]?.isRefreshReq || false;
    const job: IJob = {
      type: jobType as FieldEntitiesType,
      Details: {
        LastRefreshTime: newRefreshTime,
        NextRefreshInterval: jobDetails[jobType]?.[JobTypes.Load]?.NextRefreshInterval || 30,
        LastRefreshed: jobDetails[jobType]?.[JobTypes.Load]?.LastRefreshed || 0,
      },
      InfoText: jobType,
      // prevDataRefreshTime is Greater than new one dataRefreshed, which means job has ran and we have a updated data
      isRefreshReq: isPolling ? (isRefreshRequiredAlreadySet || prevDataRefreshTime > dataRefreshed) : false,
      isStaticData: !isValidObjectAndNotEmpty(jobDetails[jobType]),
    };
    newJobsArray.push(job);
  });
  newJobObject.headerName = 'Data Refresh';
  newJobObject.jobsArray = newJobsArray;
  newState.jobObject = newJobObject;
  newState.requestProcessing[ReportActionTypes.DATA_REFRESH_DETAILS_LOAD_REQUEST] = APIRequestStatus.Success;
  return newState;
};
// Returns the updated report state on date Refresh status success
// @jobDetails - jobinfo and status
// @ispolling - boolean to check weather the polling is required
export const dataRefreshStatusLoadSuccess = (state: IReportState, jobDetails: IDataRefreshStatus, isPolling?: boolean) => {
  const newState = state;
  const newJobObject = newState.jobObject;
  const newJobsArray = [] as IJob[];
  const namespaceTypes = Object.keys(jobDetails) as FieldEntitiesType[];
  namespaceTypes.forEach((namespace: FieldEntitiesType, index) => {
    const today = moment();
    const dataRefreshed = jobDetails[namespace].LastRefreshed || 0;
    const newRefreshTime = today.subtract(dataRefreshed, 'minutes').toDate();
    const prevDataRefreshTime = state?.jobObject?.jobsArray[index]?.Details?.LastRefreshed || dataRefreshed;
    const isRefreshRequiredAlreadySet = state?.jobObject?.jobsArray[index]?.isRefreshReq || false;
    const job: IJob = {
      type: namespace,
      Details: {
        LastRefreshTime: newRefreshTime,
        NextRefreshInterval: jobDetails[namespace].NextRefreshInterval || 30,
        LastRefreshed: jobDetails[namespace].LastRefreshed || 0,
      },
      InfoText: namespace,
      // prevDataRefreshTime is Greater than new one dataRefreshed, which means job has ran and we have a updated data
      isRefreshReq: isPolling ? (isRefreshRequiredAlreadySet || prevDataRefreshTime > dataRefreshed) : false,
      isStaticData: !isValidObjectAndNotEmpty(jobDetails[namespace]),
    };
    newJobsArray.push(job);
  });
  newJobObject.headerName = 'Data Refresh';
  newJobObject.jobsArray = newJobsArray;
  newState.jobObject = newJobObject;
  newState.requestProcessing[ReportActionTypes.DATA_REFRESH_DETAILS_LOAD_REQUEST] = APIRequestStatus.Success;
  return newState;
};
// Returns the updated report state on dynamic measure Api success
// @state - Report State
// @dynamicMeasures - New Dynamic Measures
export const updateDynmaicMeasureStateOnSuccess = (state: IReportState, dynamicMeasures: Array<DynamicMeasureResponse>) => {
  const newState = state;
  newState.isDynamicMeasuresLoaded = dynamicMeasures.length > 0;
  const updatedDynamicMeasures = dynamicMeasures.map(({
    DisplayName, Alias, DataType, Entity, EntityName, SchemaName, JoinID, Id, IsSourceCustomColumn, Namespace, NamespaceEntity, NsDisplayName, Description,
  }: DynamicMeasureResponse) => buildMeasureColumn(Id, DisplayName, Alias, DataType, 'Sum', dynamicMeasureDefaultConfig, Entity, EntityName, SchemaName, JoinID, undefined, Namespace, undefined, IsSourceCustomColumn, NamespaceEntity, NsDisplayName, Description));
  newState.allMeasures = [...newState.allMeasures, ...updatedDynamicMeasures];
  newState.requestProcessing[ReportActionTypes.REPORT_DYNAMIC_MEASURES_LOAD_REQUEST] = APIRequestStatus.Success;
  return newState;
};

// Returns the updated measures based on the columns
// @measures -  measures list
// @columns -  Array of all the columns based.
export const getUpdatedMeasures = (measures: IColumn[], columns: IColumn[]) => {
  const newAllMeasuresWithoutApplied = measures?.filter((measure) => !isMeasureAndApplied(measure));
  const newAllMeasuresWithoutAppliedAndPreDefined = newAllMeasuresWithoutApplied.filter((m) => isMeasureDynamic(m));
  const measuresFromColumns = columns?.filter((col) => isMeasure(col)
    && !newAllMeasuresWithoutAppliedAndPreDefined.some((m) => m.Id === col.Id));
  return newAllMeasuresWithoutAppliedAndPreDefined.concat(measuresFromColumns);
};

// Returns the Initial Visualization state for the charts.
export const getInitialVisualization = (configVisualization: IConfigVisualization, isAugumented: boolean):IVisualization => {
  const { Type, Builder, DisplayName } = configVisualization;
  const comboBuilder = (Builder as ReportResponseModel.IChartVisualizationBuilder)?.ComboConfig
    ? (Builder as ReportResponseModel.IChartVisualizationBuilder)
    : undefined;

  // default property from config
  if (Builder?.Properties) {
    return {
      type: Type,
      attributes: {
        showDataLabels: Builder?.Properties?.showDataLabels,
        fullStacked: Builder?.Properties?.fullStacked,
        showPercentage: Builder?.Properties?.showPercentage,
        stacked: Builder?.Properties?.stacked,
        showSharedTooltip: Builder?.Properties?.showSharedTooltip,
        hideLegend: Builder?.Properties?.hideLegend,
        showCategoryName: Builder?.Properties?.showCategoryName,
        showSeriesLabel: Builder?.Properties?.showSeriesLabel,
        showSubTotals: Builder?.Properties?.showSubTotals,
        showSummary: !(IChartAttributesKeys.showSummary in Builder?.Properties) && !isAugumented ? true : Builder?.Properties?.showSummary,
        showWrappedHeader: resolveWrappedHeader(Builder),
        // Funnel fields — saved automatically via the attributes spread; read back explicitly here.
        funnelDimensionId: Builder?.Properties?.funnelDimensionId,
        funnelMeasureId: Builder?.Properties?.funnelMeasureId,
        funnelStageOrder: Builder?.Properties?.funnelStageOrder,
        funnelHiddenStages: Builder?.Properties?.funnelHiddenStages,
        funnelPercentageMode: Builder?.Properties?.funnelPercentageMode,
      },
      ...(DisplayName ? { DisplayName } : {}),
      ...(comboBuilder ? { Builder: comboBuilder } : {}),
    };
  }

  const isPieOrDonut = isPieOrDonutChart(Type);
  return {
    type: Type,
    attributes: {
      showDataLabels: !(Builder?.Properties?.HideDataLabels ?? true),
      showPercentage: isPieOrDonut ? !(Builder?.Properties?.HideDataLabels ?? true) : false,
      fullStacked: isPieOrDonut ? false : Builder?.Properties?.StackedStyle?.toLowerCase() === 'percent',
      stacked: isPieOrDonut ? false : Builder?.Properties?.PlotStyle?.toLowerCase() === 'stacked',
      showSubTotals: Builder?.Properties?.showSubTotals,
      showSummary: (!Builder?.Properties || !(Builder?.Properties && IChartAttributesKeys.showSummary in Builder?.Properties)) && !isAugumented ? true : Builder?.Properties?.showSummary,
      showWrappedHeader: resolveWrappedHeader(Builder),
    },
    ...(DisplayName ? { DisplayName } : {}),
    ...(comboBuilder ? { Builder: comboBuilder } : {}),
  };
};

// Return the updated prop for the measure to store in the config
// @column - Measure for which we need to update the measure
export const removeBuilderPropForMeasure = (column:IColumn):IColumn => {
  if (column?.Props?.Measure?.IsMeasure) {
    return {
      Name: column.Name,
      Id: column.Id,
      Props: column?.Props,
      AllowDrillDown: column?.AllowDrillDown,
    };
  }
  return column;
};

// Returns the updated report State on drilldown sorting change
// @state - Report State
// @columnType - Type of field on which sort is applied
// @order - Order of sort
// @noMultiSort - boolean to check the multiSort
export const getStateOnDrilldownSortColumn = (
  state: IReportState,
  columnType: draggableItemType,
  columnName: string,
  order: SortingOrderTypes | null, // null implies removal of sort
  multiSort: boolean,
  Id: string,
): IReportState => {
  const newState = state;
  const {
    columns, activeDimensions, autodrilldownData,
  } = newState;
  const drilldownFields = autodrilldownData?.drilldownFields;
  const [sortState, hasChanged] = sortColumns(
    autodrilldownData.sort || [],
    activeDimensions,
    getAppliedMeasures(columns),
    columnType,
    columnName,
    order,
    !state.reportConfig.IsAugmented && multiSort,
    Id,
    drilldownFields,
    true,
  );
  if (!hasChanged) return newState;
  return {
    ...newState,
    autodrilldownData: {
      ...newState.autodrilldownData,
      sort: sortState,
    },
  };
};

// For User Multi Select Filter Updating the Metadata status if exists
const getUpdatedFilterConfig = (item:ReportResponseModel.IFilterConfig[], filterMetaData:ReportResponseModel.IFiltersMetaData) => {
  const updatedFilterConfig = [...item];
  if (isValidArrayAndNotEmpty(updatedFilterConfig)) {
    updatedFilterConfig.forEach((filter: IFilterConfig) => {
      if (filter.Type === FilterType.UserMultiSelect && filterMetaData?.Status) {
        filter.Metadata.Status = filterMetaData?.Status;
      }
    });
  }

  return updatedFilterConfig;
};

/**
 * Processes entity group dimensions and their meta columns in the report
 * @param columns - Array of report columns to be processed
 * @param activeDimensions - Array of currently active dimensions
 * @param entityGroupStore - Mapping of dimension IDs to their selected entity group options
 * @param entityGroupOptionsMap - Mapping of dimension IDs to their available entity group options
 * @returns Updated array of columns with processed entity group dimensions and meta columns
 */
export const processEntityGroupDimensions = (
  columns: Array<ReportResponseModel.IColumn>,
  activeDimensions: Array<ReportReduxModel.IDimension> = [],
  entityGroupStore: Record<string, string>,
  entityGroupOptionsMap: Record<string, IDimensionMetaField[]>,
): Array<ReportResponseModel.IColumn> => {
  const updatedColumns = cloneDeep(columns);
  Object.entries(entityGroupStore).forEach(([dimensionId, optionId]) => {
    try {
      // Get options for this dimension - skip if none
      const options = entityGroupOptionsMap[dimensionId];
      if (!isValidMetaSelectorOptions(options)) return;

      // Find the selected option - skip if not found
      const selectedOption = options.find((opt) => opt.Id === optionId);
      // Find the dimension - skip if not found
      const dimensionItem = activeDimensions.find((dim) => dim.Id === dimensionId);
      if (!dimensionItem) return;
      const isMetaColumnRequired = selectedOption.Config.length > 1;
      const existingColumnIndex = updatedColumns.findIndex((col) => col.Id === dimensionId);
      const mainColumn = createEntityGroupedColumn(dimensionItem, selectedOption.Config.at(-1), isMetaColumnRequired);
      if (existingColumnIndex >= 0) {
        updatedColumns[existingColumnIndex] = mainColumn;
      } else {
        updatedColumns.push(mainColumn);
      }
      if (isMetaColumnRequired) {
        const referTo = getMetaFieldId(dimensionId);
        const option = selectedOption.Config[0];
        const metaColumn = {
          Id: referTo,
          Name: option.Name,
          Props: {
            ...option.Props,
            ParentDimension: dimensionId,
            Action: option.Props?.Action,
            QueryId: option.Props?.QueryId || '',
          },
        };
        const existingMetaColumnIndex = updatedColumns.findIndex((col) => col.Id === referTo);
        if (existingMetaColumnIndex >= 0) {
          updatedColumns[existingMetaColumnIndex] = metaColumn;
        } else if (existingColumnIndex >= 0) {
          updatedColumns.splice(existingColumnIndex + 1, 0, metaColumn);
        } else {
          updatedColumns.push(metaColumn);
        }
      }
    } catch (error) {
      console.error(`Error processing dimension ${dimensionId}:`, error);
    }
  });

  return updatedColumns;
};

/**
 * Updates the report state with new entity grouping options
 * @param state - Current report state
 * @param payload - Payload containing updated meta selector options
 * @returns Updated report state with new entity grouping options
 */
export const getStateOnSetEntityGroupingOptions = (
  state: IReportState,
  payload: ISetMetaSelectorOptionsPayload,
): IReportState => {
  const entityGroupOptionsMap = state.entityGroupOptionsMap;
  state.entityGroupOptionsMap = getUpdatedMetaSelectorOptions(payload, entityGroupOptionsMap);
  return state;
};

/**
 * Applies entity grouping to dimensions in the report state
 * Handles both drilldown and regular dimension grouping scenarios
 * @param state - Current report state
 * @param payload - Payload containing entity grouping configuration
 * @returns Updated report state with applied entity grouping
 */
export const getStateOnApplyEntityGrouping = (
  state: IReportState,
  payload: ISetDimensionsEntityGroupingPayload,
): IReportState => {
  const {
    index, mode, data, res, activeId,
  } = payload;
  const isDrilldown = isDrilldownTabActive(activeId);
  const { entityGroupStore, appliedDimensions } = state;
  if (isDrilldown) {
    let newAppliedDrilldowns = state.autodrilldownData.drilldownFields;
    let column = cloneDeep(data) as IColumn;
    if (isValidMetaSelectorOptions(res)) {
      const selectedOption = res[0];
      column = createEntityGroupedDrilldown(column, selectedOption.Config.at(-1), DimensionStatus.Applied);
      newAppliedDrilldowns.push(column);
    } else {
      column.Props.Dimension.Applied = DimensionStatus.Applied;
      newAppliedDrilldowns = newAppliedDrilldowns.filter((d) => d.Id === column.Id);
      newAppliedDrilldowns.splice(index, 0, column);
    }
    updateEntityGroupStore(entityGroupStore, column);
    state.autodrilldownData.drilldownFields = reorderDrilldownFields(newAppliedDrilldowns);
    state.autodrilldownData.sort = rebuildSortState(state.autodrilldownData.sort, state.activeDimensions, getAppliedMeasures(state.columns), state?.autodrilldownData?.drilldownFields, state?.autodrilldownData?.sort?.length <= 1, true);
  } else {
    let dimension = cloneDeep(data) as IDimension;
    let newAppliedDimensions = cloneDeep(appliedDimensions);
    if (isValidMetaSelectorOptions(res)) {
      const selectedOption = mode === DimensionMode.RowGroup ? res[0] : res.at(-1);
      const selectedColumn = cloneDeep(selectedOption.Config.at(-1));

      const isMetaColumnPresent = selectedOption.Config.length > 1;
      dimension = createEntityGroupedDimension(dimension, selectedColumn, DimensionStatus.Applied, isMetaColumnPresent, mode);
      newAppliedDimensions = addDimensionToGrouping(appliedDimensions, dimension, selectedOption, index, isMetaColumnPresent, true);
    } else {
      dimension.Applied = DimensionStatus.Applied;
      dimension.DimensionMode = mode;
      dimension.ZeroAnchor = dimension.ZeroAnchor === ZeroAnchorStatus.Disabled
        ? ZeroAnchorStatus.Disabled
        : ZeroAnchorStatus.Enabled; // zero values un-applied by default
      newAppliedDimensions = newAppliedDimensions.filter((d) => d.Id !== dimension.Id);
      newAppliedDimensions.splice(index, 0, dimension);
    }
    updateEntityGroupStore(entityGroupStore, dimension);
    state.appliedDimensions = newAppliedDimensions;
  }
  state.entityGroupStore = entityGroupStore;
  return state;
};

/**
 * Updates the report state when entity grouping configuration changes
 * Handles both drilldown and regular dimension grouping scenarios
 * @param state - Current report state
 * @param payload - Payload containing updated entity grouping configuration
 * @returns Updated report state with modified entity grouping
 */
export const getStateOnSetEntityGrouping = (state: IReportState, payload: ISetEntityGroupingPayload) => {
  const newState = state;
  const { index, opt, activeId } = payload;
  const entityGroupStore = newState.entityGroupStore;
  const isDrilldown = isDrilldownTabActive(activeId);
  let newAppliedDimensions = isDrilldown
    ? cloneDeep(newState.autodrilldownData.drilldownFields)
    : cloneDeep(newState.appliedDimensions);
  if (opt && isValidObjectAndNotEmpty(opt.Config)) {
    newAppliedDimensions = isDrilldown
      ? updateEntityGroupedDrilldown(index, newAppliedDimensions as IColumn[], opt)
      : updateEntityGroupedDimension(index, newAppliedDimensions as IDimension[], opt, true);
    updateEntityGroupStore(entityGroupStore, newAppliedDimensions[index]);
  }
  newState.entityGroupStore = entityGroupStore;
  if (isDrilldown) {
    newState.autodrilldownData.drilldownFields = newAppliedDimensions as IColumn[];
  } else {
    newState.appliedDimensions = newAppliedDimensions as IDimension[];
  }
  return newState;
};
